/**
 * StudySync Study Dashboard Component
 * Detailed view for managing individual studies, participants, and data
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Alert,
  Container,
  Grid,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Divider,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Skeleton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Badge,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import {
  People,
  Email,
  Apple,
  Delete,
  Refresh,
  ContentCopy,
  ArrowBack,
  Edit,
  Visibility,
  AttachFile,
  CheckCircle,
  Warning,
  Send,
  ExpandMore,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { CopilotTab } from "./copilot/CopilotTab";
import { useSecureAuth } from "../../contexts/SecureAuthContext";
import { apiService } from "../../config/api";
import { StudyService } from "../../services/studyService";
import { EmailService } from "../../services/emailService";
import { ParticipantService } from "../../services/participantService";
import { FitbitService } from "../../services/fitbitService";
import DataExtractionInterface from "./DataExtractionInterface";
import HealthKitQRGenerator from "./HealthKitQRGenerator";
import NotificationPopup from "../common/NotificationPopup";
import LatestUpdatesCard from "../common/LatestUpdatesCard";
import { useNotifications } from "../../hooks/useNotifications";
import type {
  Study,
  Participant,
  EmailHistoryRecord,
  PendingParticipant,
  StudyMember,
} from "../../types";
import { formatPhoneNumber } from "../../utils/textUtils";
import AlertsTab from "./AlertsTab";
import WeeklyLogPanel from "./WeeklyLogPanel";
import TrackingIdTab from "./TrackingIdTab";
import StudyMetricsSummary from "./StudyMetricsSummary";
import { analyticsService } from "../../services/analyticsService";
import {
  parseDashboardURLParams,
  updateDashboardURL,
} from "../../utils/urlParamUtils";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  loading?: boolean;
}

type TabMenuGroup = "participants" | "data" | "adherence" | "study" | "misc" | "copilot";

type InviteParticipant = {
  email: string;
  phoneNumber: string;
};

type ParticipantInviteFormState = {
  studyName: string;
  description: string;
  duration: string;
  participants: InviteParticipant[];
};

type ParticipantContactFormState = {
  emailTitle: string;
  content: string;
  recipients: string[];
};

type ParticipantDeletionJob = {
  participantId: string;
  status?: string;
  currentStep?: string;
  progressMessage?: string;
  progressDetails?: any;
  heartbeatAt?: string;
  updatedAt?: string;
};

const TabSkeleton: React.FC = () => (
  <Box sx={{ py: 1, px: 2 }}>
    {[...Array(5)].map((_, i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        height={52}
        sx={{ mb: 1, borderRadius: 0.5 }}
      />
    ))}
  </Box>
);

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  loading,
}) => {
  return (
    <div hidden={value !== index}>
      {value === index && (
        <Box sx={{ py: 0.5 }}>{loading ? <TabSkeleton /> : children}</Box>
      )}
    </div>
  );
};

// Like TabPanel but keeps its children MOUNTED when inactive (display:none), so a stateful tab — the
// Copilot chat — preserves its transcript and inline charts across tab switches. (The default
// TabPanel unmounts on switch, which would discard the conversation.)
const KeepMountedTabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
}) => {
  const active = value === index;
  return (
    <div hidden={!active} style={{ display: active ? undefined : "none" }}>
      <Box sx={{ py: 0.5, height: "100%" }}>{children}</Box>
    </div>
  );
};

const DAY_MS = 24 * 60 * 60 * 1000;
const HISTORICAL_EXTRACTION_DAYS_PER_HOUR = 23;

type HistoricalProgressInfo =
  | {
      kind: "determinate";
      percentage: number;
      label: string;
      tooltip: React.ReactNode;
    }
  | {
      kind: "failed";
      percentage: number;
      label: string;
      tooltip: React.ReactNode;
    };

const parseUtcDate = (dateStr?: string | null): Date | null => {
  if (!dateStr) return null;
  const normalized = String(dateStr).split("T")[0];
  const parsed = new Date(`${normalized}T12:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const diffUtcDays = (later: Date, earlier: Date): number =>
  Math.max(0, Math.floor((later.getTime() - earlier.getTime()) / DAY_MS));

const formatEstimatedCompletionTime = (remainingDays: number): string => {
  if (remainingDays <= 0) {
    return "Less than 1 hour";
  }

  const estimatedHours = Math.ceil(
    remainingDays / HISTORICAL_EXTRACTION_DAYS_PER_HOUR,
  );

  if (estimatedHours <= 1) {
    return "About 1 hour";
  }

  if (estimatedHours < 24) {
    return `About ${estimatedHours} hours`;
  }

  const estimatedDays = estimatedHours / 24;

  if (estimatedDays < 2) {
    return `About ${estimatedHours} hours (~1 day)`;
  }

  return `About ${estimatedHours} hours (~${Math.ceil(estimatedDays)} days)`;
};

const buildHistoricalProgressTooltip = (
  title: string,
  details?: React.ReactNode,
) => (
  <Box sx={{ color: "inherit" }}>
    <Typography variant="body2" color="inherit" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {details}
  </Box>
);

const getHistoricalProgressInfo = (
  participant: Participant,
): HistoricalProgressInfo | null => {
  const historicalExtraction = participant.historicalExtraction;
  if (!historicalExtraction) {
    return null;
  }

  const status = historicalExtraction.status;
  const todayDateStr = new Date().toISOString().split("T")[0];

  if (status === "completed" && !historicalExtraction.memberSince) {
    return {
      kind: "determinate",
      percentage: 100,
      label: "Completed",
      tooltip: buildHistoricalProgressTooltip(
        "Historical extraction completed",
      ),
    };
  }

  if (!historicalExtraction.memberSince) {
    return {
      kind: status === "failed" ? "failed" : "determinate",
      percentage: status === "completed" ? 100 : 0,
      label:
        status === "completed"
          ? "Completed"
          : status === "failed"
            ? "Needs retry"
            : "In progress",
      tooltip:
        status === "completed"
          ? buildHistoricalProgressTooltip("Historical extraction completed")
          : status === "failed"
            ? buildHistoricalProgressTooltip(
                "Historical extraction needs retry",
              )
            : buildHistoricalProgressTooltip(
                "Historical extraction in progress",
              ),
    };
  }

  const today = parseUtcDate(todayDateStr);
  const memberSince = parseUtcDate(historicalExtraction.memberSince);
  if (!today || !memberSince) {
    return {
      kind: status === "failed" ? "failed" : "determinate",
      percentage: status === "completed" ? 100 : 0,
      label:
        status === "completed"
          ? "Completed"
          : status === "failed"
            ? "Needs retry"
            : "In progress",
      tooltip:
        status === "completed"
          ? buildHistoricalProgressTooltip("Historical extraction completed")
          : status === "failed"
            ? buildHistoricalProgressTooltip(
                "Historical extraction needs retry",
              )
            : buildHistoricalProgressTooltip(
                "Historical extraction in progress",
              ),
    };
  }

  const totalDays = Math.max(1, diffUtcDays(today, memberSince));
  const cursorDateStr =
    status === "completed"
      ? historicalExtraction.earliestDateFound ||
        historicalExtraction.memberSince
      : historicalExtraction.lastExtractedDate || todayDateStr;
  const cursorDate = parseUtcDate(cursorDateStr) || today;
  const processedDays =
    status === "completed"
      ? totalDays
      : Math.min(totalDays, diffUtcDays(today, cursorDate));
  const remainingDays = Math.max(0, totalDays - processedDays);
  const percentage =
    status === "completed"
      ? 100
      : Math.min(
          100,
          Math.max(0, Math.round((processedDays / totalDays) * 100)),
        );

  if (status === "failed") {
    return {
      kind: "failed",
      percentage,
      label: "Needs retry",
      tooltip:
        percentage > 0
          ? buildHistoricalProgressTooltip(
              `Historical extraction paused at ${percentage}%`,
            )
          : buildHistoricalProgressTooltip("Historical extraction needs retry"),
    };
  }

  return {
    kind: "determinate",
    percentage,
    label: status === "completed" ? "Completed" : "In progress",
    tooltip:
      status === "completed"
        ? buildHistoricalProgressTooltip("Historical extraction completed")
        : buildHistoricalProgressTooltip(
            `Historical extraction: ${percentage}%`,
            <>
              <Typography
                variant="caption"
                color="inherit"
                sx={{ display: "block", mt: 0.5 }}
              >
                Estimated time to completion:{" "}
                {formatEstimatedCompletionTime(remainingDays)}
              </Typography>
            </>,
          ),
  };
};

const StudyDashboard: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useSecureAuth();
  const { notification, showSuccess, showError, clearNotification } =
    useNotifications();

  const [study, setStudy] = useState<Study | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [deletingParticipantIds, setDeletingParticipantIds] = useState<
    string[]
  >([]);
  const [deletionJobsByParticipantId, setDeletionJobsByParticipantId] =
    useState<Record<string, ParticipantDeletionJob>>({});
  const [pendingParticipants, setPendingParticipants] = useState<
    PendingParticipant[]
  >([]);
  const [emailHistory, setEmailHistory] = useState<EmailHistoryRecord[]>([]);
  const [studyMembers, setStudyMembers] = useState<StudyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(() => {
    const dashParams = parseDashboardURLParams();
    return dashParams.tab ?? 0;
  });
  const [participantViewMode, setParticipantViewMode] = useState<
    "participant" | "pending"
  >("participant");
  const [tabMenuAnchor, setTabMenuAnchor] = useState<null | HTMLElement>(null);
  const [tabMenuGroup, setTabMenuGroup] = useState<TabMenuGroup | null>(null);
  const [copiedStudyCode, setCopiedStudyCode] = useState<string | null>(null);
  const loadRequestIdRef = useRef(0);

  // Tab order: 0=Participant, 1=DataExtraction, 2=WeeklyLog, 3=Alerts, 4=TrackingID,
  //            5=Email History, 6=Study Members, 7=Visualization, 8=DataCoverage
  const alertsTabIndex = 3;
  const trackingIdTabIndex = 4;

  const activeTabGroup: TabMenuGroup =
    tabValue === 9
      ? "copilot"
      : tabValue === 1 || tabValue === 7 || tabValue === 8
        ? "data"
      : tabValue === 3
        ? "adherence"
        : tabValue === 6
          ? "study"
          : tabValue === 2
            ? "misc"
            : "participants";

  // Dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [iosInviteDialogOpen, setIosInviteDialogOpen] = useState(false);
  const [iosInviteMode, setIosInviteMode] = useState<
    "qr" | "invite" | "contact"
  >("qr");
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactCollaboratorDialogOpen, setContactCollaboratorDialogOpen] =
    useState(false);

  // Unified participant email mode
  const [participantEmailMode, setParticipantEmailMode] = useState<
    "invite" | "contact"
  >("invite");

  // Unified collaborator email mode
  const [collaboratorEmailMode, setCollaboratorEmailMode] = useState<
    "invite" | "contact"
  >("invite");
  const [inviteReviewOpen, setInviteReviewOpen] = useState(false);
  const [iosInviteReviewOpen, setIosInviteReviewOpen] = useState(false);
  const [iosContactReviewOpen, setIosContactReviewOpen] = useState(false);
  const [contactReviewOpen, setContactReviewOpen] = useState(false);
  const [contactCollaboratorReviewOpen, setContactCollaboratorReviewOpen] =
    useState(false);
  const [inviteCollaboratorReviewOpen, setInviteCollaboratorReviewOpen] =
    useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState<string | null>(
    null,
  );
  const [emailViewOpen, setEmailViewOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailHistoryRecord | null>(
    null,
  );
  const [editPendingDialogOpen, setEditPendingDialogOpen] = useState(false);
  const [editParticipantDialogOpen, setEditParticipantDialogOpen] =
    useState(false);
  const [deletePendingDialogOpen, setDeletePendingDialogOpen] = useState(false);
  const [pendingParticipantToDelete, setPendingParticipantToDelete] = useState<
    string | null
  >(null);
  const [pendingParticipantToEdit, setPendingParticipantToEdit] =
    useState<PendingParticipant | null>(null);
  const [participantToEdit, setParticipantToEdit] = useState<Participant | null>(
    null,
  );
  const [participantDetailsDialogOpen, setParticipantDetailsDialogOpen] =
    useState(false);
  const [selectedParticipantDetails, setSelectedParticipantDetails] =
    useState<any>(null);

  // Loading states
  const [editLoading, setEditLoading] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);

  // Form states for invitation
  const [inviteForm, setInviteForm] = useState<ParticipantInviteFormState>({
    studyName: "",
    description: "",
    duration: "",
    participants: [{ email: "", phoneNumber: "" }],
  });

  const [iosInviteForm, setIosInviteForm] =
    useState<ParticipantInviteFormState>({
      studyName: "",
      description: "",
      duration: "",
      participants: [{ email: "", phoneNumber: "" }],
    });

  // Form states for contact participants
  const [contactForm, setContactForm] = useState<ParticipantContactFormState>({
    emailTitle: "",
    content: "",
    recipients: [],
  });

  const [iosContactForm, setIosContactForm] =
    useState<ParticipantContactFormState>({
      emailTitle: "",
      content: "",
      recipients: [],
    });

  // Form states for contact collaborator
  const [contactCollaboratorForm, setContactCollaboratorForm] = useState({
    emailTitle: "",
    content: "",
    recipients: [] as string[],
  });

  // Re-auth (Fitbit: participants missing SpO2/breathing scope)
  type ParticipantNeedingReauth = {
    participantId: string;
    participantEmail?: string;
    firstName?: string;
    lastName?: string;
    scope?: string;
  };
  const [participantsNeedingReauth, setParticipantsNeedingReauth] = useState<
    ParticipantNeedingReauth[]
  >([]);
  const [reauthAnchorEl, setReauthAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [reauthSendDialogOpen, setReauthSendDialogOpen] = useState(false);
  const [reauthSendParticipant, setReauthSendParticipant] =
    useState<ParticipantNeedingReauth | null>(null);
  const [reauthEmailSubject, setReauthEmailSubject] = useState("");
  const [reauthEmailBody, setReauthEmailBody] = useState("");
  const [reauthSending, setReauthSending] = useState(false);

  // Form states for invite collaborator
  const [inviteCollaboratorForm, setInviteCollaboratorForm] = useState({
    emailTitle: "",
    content: "",
    recipients: [] as string[],
  });

  // Review form states for editing in review mode
  const [inviteReviewForm, setInviteReviewForm] = useState({
    emailTitle: "",
    content: "",
  });

  const [iosInviteReviewForm, setIosInviteReviewForm] = useState({
    emailTitle: "",
    content: "",
  });

  // Review form states for invite collaborator
  const [inviteCollaboratorReviewForm, setInviteCollaboratorReviewForm] =
    useState({
      emailTitle: "",
      content: "",
    });

  const [contactReviewForm, setContactReviewForm] = useState({
    emailTitle: "",
    content: "",
  });

  const [iosContactReviewForm, setIosContactReviewForm] = useState({
    emailTitle: "",
    content: "",
  });

  const [contactCollaboratorReviewForm, setContactCollaboratorReviewForm] =
    useState({
      emailTitle: "",
      content: "",
    });

  // Form states for edit study
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    duration: "",
  });

  // Form states for pending participants
  const [editPendingForm, setEditPendingForm] = useState({
    phoneNumber: "",
  });
  const [editParticipantForm, setEditParticipantForm] = useState({
    firstName: "",
    lastName: "",
  });

  useEffect(() => {
    if (studyId) {
      loadStudyData();
    }
  }, [studyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyDeletionJobs = (jobs: ParticipantDeletionJob[]) => {
    setDeletingParticipantIds(jobs.map((job) => job.participantId));
    setDeletionJobsByParticipantId(
      jobs.reduce<Record<string, ParticipantDeletionJob>>(
        (accumulator, job) => {
          accumulator[job.participantId] = job;
          return accumulator;
        },
        {},
      ),
    );
  };

  // Clamp tabValue to the max available tab index for the current build.
  useEffect(() => {
    const maxIndex = 9;
    if (tabValue > maxIndex) {
      setTabValue(0);
      updateDashboardURL({ tab: 0 });
    }
  }, [tabValue]);

  // Refetch participants needing re-auth when switching to Participants tab so list is fresh after onboarding
  useEffect(() => {
    if (tabValue !== 0 || !studyId) return;
    let cancelled = false;
    (async () => {
      const token = await apiService.getAuthToken();
      if (!token || cancelled) return;
      try {
        const reauthResponse: any =
          await FitbitService.getParticipantsNeedingReauth(studyId, token);
        if (
          !cancelled &&
          reauthResponse?.success &&
          Array.isArray(reauthResponse.participants)
        ) {
          setParticipantsNeedingReauth(reauthResponse.participants);
        } else if (!cancelled) {
          setParticipantsNeedingReauth([]);
        }
      } catch {
        if (!cancelled) setParticipantsNeedingReauth([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabValue, studyId]);

  const loadStudyData = async () => {
    if (!studyId) return;
    const requestId = ++loadRequestIdRef.current;

    try {
      setLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to load study data",
          "Load Study",
        );
        return;
      }

      const [
        studyResult,
        participantsResult,
        emailResult,
        pendingResult,
        membersResult,
      ] = await Promise.allSettled([
        StudyService.getStudy(studyId, token),
        StudyService.getStudyParticipants(studyId, token),
        EmailService.getEmailHistory(studyId, token),
        StudyService.getPendingParticipants(studyId, token),
        StudyService.getStudyMembers(studyId, token),
      ]);

      if (requestId !== loadRequestIdRef.current) {
        return;
      }

      if (studyResult.status === "fulfilled") {
        const studyResponse: any = studyResult.value;
        if (studyResponse.success && studyResponse.study) {
          setStudy(studyResponse.study);
        }
      }

      if (participantsResult.status === "fulfilled") {
        const participantsResponse: any = participantsResult.value;
        if (participantsResponse.success && participantsResponse.participants) {
          setParticipants(participantsResponse.participants);
        } else {
          setParticipants([]);
        }
      }

      if (emailResult.status === "fulfilled") {
        const emailResponse: any = emailResult.value;
        if (emailResponse.success && emailResponse.history) {
          setEmailHistory(emailResponse.history);
        } else {
          setEmailHistory([]);
        }
      }

      if (pendingResult.status === "fulfilled") {
        const pendingResponse: any = pendingResult.value;
        if (pendingResponse.success && pendingResponse.data) {
          setPendingParticipants(pendingResponse.data);
        } else {
          setPendingParticipants([]);
        }
      }

      if (membersResult.status === "fulfilled") {
        const membersResponse: any = membersResult.value;
        if (membersResponse.success && membersResponse.members) {
          setStudyMembers(membersResponse.members);
        } else {
          setStudyMembers([]);
        }
      }

      if (studyResult.status === "fulfilled") {
        const studyResponse: any = studyResult.value;
        if (studyResponse.success && studyResponse.study?.studyCode) {
          try {
            const deletionJobsResponse =
              await ParticipantService.getActiveDeletionJobs(
                studyResponse.study.studyCode,
                token,
              );
            if (
              deletionJobsResponse.success &&
              Array.isArray(deletionJobsResponse.jobs)
            ) {
              applyDeletionJobs(
                deletionJobsResponse.jobs as ParticipantDeletionJob[],
              );
            } else {
              applyDeletionJobs([]);
            }
          } catch {
            applyDeletionJobs([]);
          }
        }
      }

      const firstRejected = [
        studyResult,
        participantsResult,
        emailResult,
        pendingResult,
        membersResult,
      ].find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );

      if (firstRejected && studyResult.status !== "fulfilled") {
        throw firstRejected.reason;
      }
    } catch (err: any) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      showError(
        "Failed to Load Study",
        err.message || "An unexpected error occurred while loading study data",
        "Load Study",
      );
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!study?.studyCode || deletingParticipantIds.length === 0) {
      return;
    }

    let cancelled = false;
    let pollCount = 0;
    let timeoutId: number | null = null;

    const scheduleNextPoll = () => {
      const nextDelay = pollCount < 3 ? 5000 : 15000;
      timeoutId = window.setTimeout(runPoll, nextDelay);
    };

    const runPoll = async () => {
      const token = await apiService.getAuthToken();
      if (!token || cancelled) {
        return;
      }

      try {
        const deletionJobsResponse =
          await ParticipantService.getActiveDeletionJobs(
            study.studyCode,
            token,
          );
        if (cancelled) {
          return;
        }
        const nextDeletingJobs =
          deletionJobsResponse.success &&
          Array.isArray((deletionJobsResponse as any).jobs)
            ? ((deletionJobsResponse as any).jobs as ParticipantDeletionJob[])
            : [];
        const hadDeleting = deletingParticipantIds.length > 0;
        applyDeletionJobs(nextDeletingJobs);
        if (hadDeleting && nextDeletingJobs.length === 0) {
          await loadStudyData();
          return;
        }
        pollCount += 1;
      } catch {
        // Keep current UI state; next poll can recover.
        pollCount += 1;
      }
      if (!cancelled) {
        scheduleNextPoll();
      }
    };

    scheduleNextPoll();

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [study?.studyCode, deletingParticipantIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditStudy = async () => {
    if (!study || !editForm.name.trim()) {
      showError("Validation Error", "Study name is required", "Update Study");
      return;
    }

    try {
      setEditLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to update study",
          "Update Study",
        );
        return;
      }

      const response = await StudyService.updateStudy(
        study.id,
        {
          name: editForm.name.trim(),
          description: editForm.description.trim(),
          duration: editForm.duration.trim() || undefined,
        },
        token,
      );

      if (response.success) {
        // Immediately update the local state with the new values
        if (study) {
          const updatedStudy = {
            ...study,
            name: editForm.name.trim(),
            description: editForm.description.trim(),
            duration: editForm.duration.trim(),
          };
          setStudy(updatedStudy);
        }

        setEditDialogOpen(false);
        showSuccess(
          "Study Updated",
          "Study information has been updated successfully",
          "Update Study",
        );
        await loadStudyData();
      } else {
        showError(
          "Failed to Update Study",
          response.error || "An error occurred while updating the study",
          "Update Study",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Update Study",
        err.message || "An unexpected error occurred while updating the study",
        "Update Study",
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenReauthSend = async (p: ParticipantNeedingReauth) => {
    setReauthAnchorEl(null);
    const token = await apiService.getAuthToken();
    if (!token || !study?.studyCode) {
      showError(
        "Error",
        "Cannot generate re-auth link. Please try again.",
        "Re-auth",
      );
      return;
    }
    try {
      const response: any = await FitbitService.getReauthAuthUrl(
        p.participantId,
        study.studyCode,
        token,
      );
      const authUrl = response?.authUrl;
      if (!authUrl) {
        showError("Error", "Failed to get re-auth link.", "Re-auth");
        return;
      }
      setReauthSendParticipant(p);
      setReauthEmailSubject(
        `Action needed: Re-authorize Fitbit for ${study.name || "your study"}`,
      );
      setReauthEmailBody(
        `Hello,\n\nOur study is now collecting new data metrics from Fitbit. To enable these, please re-authorize your Fitbit connection by clicking the link below:\n\n${authUrl}\n\nThis will open Fitbit's consent page where you can grant the new permissions. Thank you.`,
      );
      setReauthSendDialogOpen(true);
    } catch (err: any) {
      showError(
        "Error",
        err?.message || "Failed to get re-auth link.",
        "Re-auth",
      );
    }
  };

  const handleSendReauthMessage = async () => {
    if (!study || !reauthSendParticipant?.participantEmail) {
      showError("Error", "No recipient selected.", "Send");
      return;
    }
    if (!reauthEmailSubject.trim() || !reauthEmailBody.trim()) {
      showError(
        "Validation Error",
        "Subject and message are required.",
        "Send",
      );
      return;
    }
    setReauthSending(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError("Authentication Error", "Please sign in again.", "Send");
        return;
      }
      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: [{ email: reauthSendParticipant.participantEmail }],
          emailSubject: reauthEmailSubject,
          emailBody: reauthEmailBody,
          invitationType: "participant",
          emailMode: "contact",
        },
        token,
      );
      if (response.success) {
        setReauthSendDialogOpen(false);
        setReauthSendParticipant(null);
        showSuccess(
          "Message sent",
          "Re-auth message was sent to the participant.",
          "Send",
        );
        await loadStudyData();
      } else {
        showError(
          "Send failed",
          (response as any).error || "Could not send message.",
          "Send",
        );
      }
    } catch (err: any) {
      showError(
        "Send failed",
        (err as any)?.message || "Could not send message.",
        "Send",
      );
    } finally {
      setReauthSending(false);
    }
  };

  const handleContactParticipants = async () => {
    if (
      !study ||
      !contactForm.emailTitle.trim() ||
      !contactForm.content.trim()
    ) {
      showError(
        "Validation Error",
        "Email title and content are required",
        "Send Emails",
      );
      return;
    }

    if (contactForm.recipients.length === 0) {
      showError(
        "Validation Error",
        "Please select at least one recipient",
        "Send Emails",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send emails",
          "Send Emails",
        );
        return;
      }

      // Convert participant IDs to emails for sending
      const participantEmails = contactForm.recipients.map((recipient) => {
        const participant = participants.find(
          (p) => p.participantId === recipient,
        );
        return participant ? participant.participantEmail : recipient;
      });

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: participantEmails.map((email) => ({ email })),
          emailSubject: contactReviewForm.emailTitle,
          emailBody: contactReviewForm.content,
          invitationType: "participant",
          emailMode: participantEmailMode,
        },
        token,
      );

      if (response.success) {
        setContactDialogOpen(false);
        setContactForm({
          emailTitle: "",
          content: "",
          recipients: [],
        });
        setContactReviewForm({
          emailTitle: "",
          content: "",
        });
        showSuccess(
          "Emails Sent",
          "Invitation emails have been sent successfully to participants",
          "Send Emails",
        );
        await loadStudyData(); // Reload data
      } else {
        showError(
          "Failed to Send Emails",
          response.error || "An error occurred while sending invitation emails",
          "Send Emails",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Emails",
        err.message || "An unexpected error occurred while sending emails",
        "Send Emails",
      );
    }
  };

  const handleInviteCollaborator = async () => {
    if (
      !study ||
      !inviteCollaboratorForm.emailTitle.trim() ||
      !inviteCollaboratorForm.content.trim()
    ) {
      showError(
        "Validation Error",
        "Email title and content are required",
        "Send Invitations",
      );
      return;
    }

    if (inviteCollaboratorForm.recipients.length === 0) {
      showError(
        "Validation Error",
        "Please select at least one recipient",
        "Send Invitations",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send invitations",
          "Send Invitations",
        );
        return;
      }

      const response = await EmailService.sendCollaboratorInvitations(
        study.studyCode,
        study.name,
        study.password || "",
        inviteCollaboratorForm.recipients,
        token,
        inviteCollaboratorReviewForm.emailTitle,
        inviteCollaboratorReviewForm.content,
      );

      if (response.success) {
        setContactCollaboratorDialogOpen(false);
        setInviteCollaboratorForm({
          emailTitle: "",
          content: "",
          recipients: [],
        });
        setInviteCollaboratorReviewForm({
          emailTitle: "",
          content: "",
        });
        showSuccess(
          "Invitations Sent",
          "Collaborator invitation emails have been sent successfully with pre-filled study information",
          "Send Invitations",
        );
        await loadStudyData(); // Reload data
      } else {
        showError(
          "Failed to Send Invitations",
          response.error ||
            "An error occurred while sending invitations to collaborators",
          "Send Invitations",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Invitations",
        err.message || "An unexpected error occurred while sending invitations",
        "Send Invitations",
      );
    }
  };

  const handleContactCollaborator = async () => {
    if (
      !study ||
      !contactCollaboratorForm.emailTitle.trim() ||
      !contactCollaboratorForm.content.trim()
    ) {
      showError(
        "Validation Error",
        "Email title and content are required",
        "Send Emails",
      );
      return;
    }

    if (contactCollaboratorForm.recipients.length === 0) {
      showError(
        "Validation Error",
        "Please select at least one recipient",
        "Send Emails",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send emails",
          "Send Emails",
        );
        return;
      }

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: contactCollaboratorForm.recipients.map((email) => ({
            email,
          })),
          emailSubject: contactCollaboratorReviewForm.emailTitle,
          emailBody: contactCollaboratorReviewForm.content,
          invitationType: "collaborator",
          emailMode: collaboratorEmailMode,
        },
        token,
      );

      if (response.success) {
        setContactCollaboratorDialogOpen(false);
        setContactCollaboratorForm({
          emailTitle: "",
          content: "",
          recipients: [],
        });
        setContactCollaboratorReviewForm({
          emailTitle: "",
          content: "",
        });
        showSuccess(
          "Emails Sent",
          "Emails have been sent successfully to collaborators",
          "Send Emails",
        );
        await loadStudyData(); // Reload data
      } else {
        showError(
          "Failed to Send Emails",
          response.error ||
            "An error occurred while sending emails to collaborators",
          "Send Emails",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Emails",
        err.message ||
          "An unexpected error occurred while sending emails to collaborators",
        "Send Emails",
      );
    }
  };

  const handleSendInvitations = async () => {
    if (!study || !inviteForm.participants.length) {
      showError(
        "Validation Error",
        "Please add at least one participant",
        "Send Invitations",
      );
      return;
    }

    // Validate all participants have email addresses
    const validParticipants = inviteForm.participants.filter((p) =>
      p.email.trim(),
    );
    if (validParticipants.length === 0) {
      showError(
        "Validation Error",
        "Please enter participant email addresses",
        "Send Invitations",
      );
      return;
    }

    if (!validParticipants.every((p) => EmailService.validateEmail(p.email))) {
      showError(
        "Validation Error",
        "Please enter valid email addresses",
        "Send Invitations",
      );
      return;
    }

    // Validate phone numbers
    const invalidPhoneParticipants = validParticipants.filter(
      (p) => p.phoneNumber && !EmailService.validatePhoneNumber(p.phoneNumber),
    );
    if (invalidPhoneParticipants.length > 0) {
      showError(
        "Validation Error",
        "Please enter valid phone numbers in format (000)-0000-000",
        "Send Invitations",
      );
      return;
    }

    // Validate required fields
    if (!inviteForm.studyName && !study.name) {
      showError(
        "Validation Error",
        "Study name is required",
        "Send Invitations",
      );
      return;
    }

    if (!inviteForm.description && !study.description) {
      showError(
        "Validation Error",
        "Study description is required",
        "Send Invitations",
      );
      return;
    }

    setInviteSending(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send invitations",
          "Send Invitations",
        );
        return;
      }

      // Sending email with data:
      // studyCode: study.studyCode,
      // studyName: inviteForm.studyName || study.name,
      // participants: inviteForm.participantEmails.map(email => ({ email })),
      // emailSubject: inviteReviewForm.emailTitle,
      // emailBody: inviteReviewForm.content,

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: validParticipants.map((p) => ({
            email: p.email,
            phoneNumber: p.phoneNumber || undefined,
          })),
          emailSubject: inviteReviewForm.emailTitle,
          emailBody: inviteReviewForm.content,
          invitationType: "participant",
          emailMode: participantEmailMode,
        },
        token,
      );

      if (response.success) {
        setInviteReviewOpen(false);
        setInviteDialogOpen(false);
        setInviteForm({
          studyName: "",
          description: "",
          duration: "",
          participants: [],
        });
        setInviteReviewForm({
          emailTitle: "",
          content: "",
        });
        showSuccess(
          "Invitations Sent",
          "Invitation emails have been sent successfully to participants",
          "Send Invitations",
        );
        await loadStudyData(); // Reload data
      } else {
        showError(
          "Failed to Send Invitations",
          response.error || "An error occurred while sending invitations",
          "Send Invitations",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Invitations",
        err.message || "An unexpected error occurred while sending invitations",
        "Send Invitations",
      );
    } finally {
      setInviteSending(false);
    }
  };

  const handleSendIosInvitations = async () => {
    if (!study || !iosInviteForm.participants.length) {
      showError(
        "Validation Error",
        "Please add at least one participant",
        "Send Invitations",
      );
      return;
    }

    const validParticipants = iosInviteForm.participants.filter((p) =>
      p.email.trim(),
    );
    if (validParticipants.length === 0) {
      showError(
        "Validation Error",
        "Please enter participant email addresses",
        "Send Invitations",
      );
      return;
    }

    if (!validParticipants.every((p) => EmailService.validateEmail(p.email))) {
      showError(
        "Validation Error",
        "Please enter valid email addresses",
        "Send Invitations",
      );
      return;
    }

    const invalidPhoneParticipants = validParticipants.filter(
      (p) => p.phoneNumber && !EmailService.validatePhoneNumber(p.phoneNumber),
    );
    if (invalidPhoneParticipants.length > 0) {
      showError(
        "Validation Error",
        "Please enter valid phone numbers in format (000)-0000-000",
        "Send Invitations",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send invitations",
          "Send Invitations",
        );
        return;
      }

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: validParticipants.map((p) => ({
            email: p.email,
            phoneNumber: p.phoneNumber || undefined,
          })),
          emailSubject: iosInviteReviewForm.emailTitle,
          emailBody: iosInviteReviewForm.content,
          invitationType: "participant",
          emailMode: "invite",
          dataSource: "healthkit",
        },
        token,
      );

      if (response.success) {
        setIosInviteDialogOpen(false);
        setIosInviteMode("qr");
        setIosInviteForm({
          studyName: "",
          description: "",
          duration: "",
          participants: [{ email: "", phoneNumber: "" }],
        });
        setIosInviteReviewForm({
          emailTitle: "",
          content: "",
        });
        setIosInviteReviewOpen(false);
        showSuccess(
          "Emails Sent",
          "Invitation emails have been sent successfully to iOS participants",
          "Send Emails",
        );
        await loadStudyData();
      } else {
        showError(
          "Email Error",
          "Failed to send invitation emails",
          "Send Invitations",
        );
      }
    } catch (_error) {
      showError(
        "Email Error",
        "Failed to send invitation emails",
        "Send Invitations",
      );
    }
  };

  const handleContactIosParticipants = async () => {
    if (
      !study ||
      !iosContactForm.emailTitle.trim() ||
      !iosContactForm.content.trim()
    ) {
      showError(
        "Validation Error",
        "Email title and content are required",
        "Send Emails",
      );
      return;
    }

    if (iosContactForm.recipients.length === 0) {
      showError(
        "Validation Error",
        "Please select at least one recipient",
        "Send Emails",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send emails",
          "Send Emails",
        );
        return;
      }

      const participantEmails = iosContactForm.recipients.map((recipient) => {
        const participant = participants.find(
          (p) => p.participantId === recipient,
        );
        return participant ? participant.participantEmail : recipient;
      });

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: participantEmails.map((email) => ({ email })),
          emailSubject: iosContactReviewForm.emailTitle,
          emailBody: iosContactReviewForm.content,
          invitationType: "participant",
          emailMode: "contact",
          dataSource: "healthkit",
        },
        token,
      );

      if (response.success) {
        setIosInviteDialogOpen(false);
        setIosInviteMode("qr");
        setIosContactForm({
          emailTitle: "",
          content: "",
          recipients: [],
        });
        setIosContactReviewForm({
          emailTitle: "",
          content: "",
        });
        setIosContactReviewOpen(false);
        showSuccess(
          "Emails Sent",
          "Participant emails have been sent successfully",
          "Send Emails",
        );
        await loadStudyData();
      } else {
        showError(
          "Failed to Send Emails",
          response.error || "An error occurred while sending emails",
          "Send Emails",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Emails",
        err.message || "An unexpected error occurred while sending emails",
        "Send Emails",
      );
    }
  };

  const renderParticipantInviteForm = (
    form: ParticipantInviteFormState,
    setForm: React.Dispatch<React.SetStateAction<ParticipantInviteFormState>>,
  ) => (
    <>
      <TextField
        label="Study Name"
        fullWidth
        variant="outlined"
        value={form.studyName}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            studyName: e.target.value,
          }))
        }
        placeholder={study?.name}
        helperText="Leave blank to use default study name"
      />

      <TextField
        label="Description"
        fullWidth
        multiline
        rows={3}
        variant="outlined"
        value={form.description}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            description: e.target.value,
          }))
        }
        placeholder={study?.description || "Enter study description"}
        helperText="Leave blank to use default study description"
      />

      <TextField
        label="Duration"
        fullWidth
        variant="outlined"
        value={form.duration}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            duration: e.target.value,
          }))
        }
        placeholder="e.g., 4 weeks, 2 months"
      />

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add Participants
        </Typography>
        {form.participants.map((participant, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mb: 2,
            }}
          >
            {index === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                * At least one participant is required
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <TextField
                fullWidth
                label="Email Address"
                value={participant.email}
                onChange={(e) => {
                  const newParticipants = [...form.participants];
                  newParticipants[index].email = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    participants: newParticipants,
                  }));
                }}
                placeholder="participant@example.com"
                required
                type="email"
                autoFocus={index === 0}
                variant="outlined"
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                fullWidth
                label="Phone Number (Optional)"
                value={participant.phoneNumber}
                onChange={(e) => {
                  const newParticipants = [...form.participants];
                  newParticipants[index].phoneNumber = formatPhoneNumber(
                    e.target.value,
                  );
                  setForm((prev) => ({
                    ...prev,
                    participants: newParticipants,
                  }));
                }}
                placeholder="(000)-0000-000"
                variant="outlined"
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
              {form.participants.length > 1 && index > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "56px",
                    width: "110px",
                    cursor: "pointer",
                    flexShrink: 0,
                    "&:hover": {
                      opacity: 0.7,
                    },
                    transition: "opacity 0.2s",
                  }}
                  onClick={() => {
                    const newParticipants = form.participants.filter(
                      (_, i) => i !== index,
                    );
                    setForm((prev) => ({
                      ...prev,
                      participants: newParticipants,
                    }));
                  }}
                  title="Remove participant"
                >
                  <Delete sx={{ color: "error.main", fontSize: "1.5rem" }} />
                </Box>
              ) : null}
              {index === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1.5,
                    py: 1,
                    backgroundColor: "primary.light",
                    borderRadius: 1,
                    height: "56px",
                    width: "110px",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="primary.contrastText"
                    sx={{ fontWeight: 600 }}
                  >
                    Required
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ))}
        <Button
          variant="outlined"
          startIcon={<People />}
          onClick={() => {
            setForm((prev) => ({
              ...prev,
              participants: [
                ...prev.participants,
                { email: "", phoneNumber: "" },
              ],
            }));
          }}
          sx={{ mb: 2 }}
        >
          Add Another Participant
        </Button>
      </Box>
    </>
  );

  const renderParticipantContactForm = (
    form: ParticipantContactFormState,
    setForm: React.Dispatch<React.SetStateAction<ParticipantContactFormState>>,
  ) => (
    <>
      <TextField
        label="Email Title"
        fullWidth
        variant="outlined"
        value={form.emailTitle}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            emailTitle: e.target.value,
          }))
        }
        placeholder="Enter email subject"
        required
      />

      <TextField
        label="Email Content"
        fullWidth
        multiline
        rows={6}
        variant="outlined"
        value={form.content}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            content: e.target.value,
          }))
        }
        placeholder="Enter your message to participants..."
        required
      />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Recipients
        </Typography>

        {participants.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select from existing participants:
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
              }}
            >
              {participants.map((participant) => (
                <Box
                  key={participant.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 0.5,
                  }}
                >
                  <Checkbox
                    checked={form.recipients.includes(
                      participant.participantId,
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setForm((prev) => ({
                          ...prev,
                          recipients: [
                            ...prev.recipients,
                            participant.participantId,
                          ],
                        }));
                      } else {
                        setForm((prev) => ({
                          ...prev,
                          recipients: prev.recipients.filter(
                            (id) => id !== participant.participantId,
                          ),
                        }));
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {participant.participantId}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={form.recipients.filter(
            (recipient) =>
              !participants.some((p) => p.participantId === recipient),
          )}
          onChange={(event, newValue) => {
            const validEmails = newValue.filter((email: string) => {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              return emailRegex.test(email);
            });
            const existingParticipants = form.recipients.filter((recipient) =>
              participants.some((p) => p.participantId === recipient),
            );
            setForm((prev) => ({
              ...prev,
              recipients: [...existingParticipants, ...validEmails],
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Additional Email Addresses"
              placeholder="Enter additional email addresses (press Enter after each)"
              helperText="Enter valid email addresses for non-participants"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={index}
              />
            ))
          }
        />
      </Box>
    </>
  );

  const getParticipantStatus = (participant: any) => {
    // Check if participant has authorized any data source (Fitbit or HealthKit)
    if (participant.authorizationStatus) {
      // Check Fitbit tokens
      if (participant.fitbitTokens?.accessToken) {
        if (participant.fitbitTokens.expiresAt) {
          const expiryDate = new Date(participant.fitbitTokens.expiresAt);
          if (expiryDate > new Date()) {
            return "active";
          } else {
            return "inactive"; // Token expired
          }
        }
        return "active"; // Has token, no expiry info
      }

      // Check HealthKit tokens
      if (participant.healthkit?.authorized) {
        return "active"; // HealthKit authorized
      }

      // Has authorization but no tokens yet (recently authorized)
      return "active";
    }
    return "inactive";
  };

  const getParticipantStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "warning";
      case "enrolled":
        return "info";
      default:
        return "default";
    }
  };

  const handleDeleteParticipant = async (participantId: string) => {
    try {
      setLoading(true);

      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to delete participant",
          "Delete Participant",
        );
        return;
      }

      const response = await ParticipantService.deleteParticipant(
        participantId,
        token,
      );

      if (response.success) {
        setDeletingParticipantIds((prev) =>
          prev.includes(participantId) ? prev : [...prev, participantId],
        );
        setDeletionJobsByParticipantId((prev) => ({
          ...prev,
          [participantId]: {
            participantId,
            status: "queued",
            currentStep: "queued",
            progressMessage: "Deletion request queued",
          },
        }));
        analyticsService.trackParticipantDeleted({ studyId: studyId || "" });
        showSuccess(
          "Participant Deletion Started",
          "Participant is being deleted in the background",
          "Delete Participant",
        );
      } else {
        showError(
          "Failed to Delete Participant",
          response.error || "An error occurred while deleting the participant",
          "Delete Participant",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Delete Participant",
        err.message ||
          "An unexpected error occurred while deleting the participant",
        "Delete Participant",
      );
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setParticipantToDelete(null);
    }
  };

  const openDeleteDialog = (participantId: string) => {
    setParticipantToDelete(participantId);
    setDeleteDialogOpen(true);
  };

  const copyStudyCode = async () => {
    if (study?.studyCode) {
      try {
        await navigator.clipboard.writeText(study.studyCode);
        setCopiedStudyCode(study.studyCode);
        setTimeout(() => setCopiedStudyCode(null), 2000);
      } catch (err) {
        // Silently fail - user can try again
      }
    }
  };

  const handleOpenVisualizations = () => {
    if (!studyId) return;
    navigate(`/researcher/study/${studyId}/visualizations`);
  };

  const handleOpenDataCoverage = () => {
    if (!studyId) return;
    navigate(`/researcher/study/${studyId}/data-coverage`);
  };

  const handleOpenConsentManagement = () => {
    if (!studyId) return;
    navigate(`/researcher/study/${studyId}/consent-management`);
  };

  const closeTabMenu = () => {
    setTabMenuAnchor(null);
    setTabMenuGroup(null);
  };

  const openTabMenu = (
    group: TabMenuGroup,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setTabMenuGroup(group);
    setTabMenuAnchor(event.currentTarget);
  };

  const selectTabValue = (nextTabValue: number) => {
    setTabValue(nextTabValue);
    updateDashboardURL({ tab: nextTabValue });
    closeTabMenu();
  };

  // Pending Participants Handlers

  const handleEditPendingParticipant = async () => {
    if (!pendingParticipantToEdit) {
      showError(
        "Validation Error",
        "No participant selected",
        "Edit Participant",
      );
      return;
    }

    try {
      setEditLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to edit participant",
          "Edit Participant",
        );
        return;
      }

      const response = await StudyService.updatePendingParticipant(
        study!.id,
        pendingParticipantToEdit.participantId,
        {
          phoneNumber: editPendingForm.phoneNumber.trim() || undefined,
        },
        token,
      );

      if (response.success) {
        setEditPendingDialogOpen(false);
        setEditPendingForm({ phoneNumber: "" });
        setPendingParticipantToEdit(null);
        showSuccess(
          "Participant Updated",
          "Pending participant has been updated successfully",
          "Edit Participant",
        );
        await loadStudyData();
      } else {
        showError(
          "Failed to Update Participant",
          response.error || "An error occurred while updating the participant",
          "Edit Participant",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Update Participant",
        err.message ||
          "An unexpected error occurred while updating the participant",
        "Edit Participant",
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditParticipant = async () => {
    if (!participantToEdit) {
      showError(
        "Validation Error",
        "No participant selected",
        "Edit Participant",
      );
      return;
    }

    const firstName = editParticipantForm.firstName.trim();
    const lastName = editParticipantForm.lastName.trim();

    if (!firstName || !lastName) {
      showError(
        "Validation Error",
        "First name and last name are required",
        "Edit Participant",
      );
      return;
    }

    try {
      setEditLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to edit participant",
          "Edit Participant",
        );
        return;
      }

      const response = await ParticipantService.updateParticipant(
        participantToEdit.participantId,
        {
          participantEmail: participantToEdit.participantEmail,
          studyCode: participantToEdit.studyCode,
          firstName,
          lastName,
        },
        token,
      );

      if (response.success) {
        setEditParticipantDialogOpen(false);
        setParticipantToEdit(null);
        setEditParticipantForm({ firstName: "", lastName: "" });
        showSuccess(
          "Participant Updated",
          "Participant name has been updated successfully",
          "Edit Participant",
        );
        await loadStudyData();
      } else {
        showError(
          "Failed to Update Participant",
          response.error || "An error occurred while updating the participant",
          "Edit Participant",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Update Participant",
        err.message ||
          "An unexpected error occurred while updating the participant",
        "Edit Participant",
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeletePendingParticipant = async (participantId: string) => {
    try {
      setLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to delete participant",
          "Delete Participant",
        );
        return;
      }

      const response = await StudyService.removePendingParticipant(
        study!.id,
        participantId,
        token,
      );

      if (response.success) {
        await loadStudyData();
        showSuccess(
          "Participant Removed",
          "Pending participant has been removed successfully",
          "Delete Participant",
        );
      } else {
        showError(
          "Failed to Remove Participant",
          response.error || "An error occurred while removing the participant",
          "Delete Participant",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Remove Participant",
        err.message ||
          "An unexpected error occurred while removing the participant",
        "Delete Participant",
      );
    } finally {
      setLoading(false);
      setDeletePendingDialogOpen(false);
      setPendingParticipantToDelete(null);
    }
  };

  const openEditPendingDialog = (participant: PendingParticipant) => {
    setPendingParticipantToEdit(participant);
    setEditPendingForm({
      phoneNumber: participant.phoneNumber || "",
    });
    setEditPendingDialogOpen(true);
  };

  const openEditParticipantDialog = (participant: Participant) => {
    setParticipantToEdit(participant);
    setEditParticipantForm({
      firstName: participant.firstName || "",
      lastName: participant.lastName || "",
    });
    setEditParticipantDialogOpen(true);
  };

  const openDeletePendingDialog = (participantId: string) => {
    setPendingParticipantToDelete(participantId);
    setDeletePendingDialogOpen(true);
  };

  const openParticipantDetailsDialog = async (participant: Participant) => {
    try {
      setLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to view participant details",
          "View Details",
        );
        return;
      }

      // Fetch detailed participant information
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/participants/${participant.participantId}/info`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to fetch participant details",
        );
      }

      const participantDetails = await response.json();
      setSelectedParticipantDetails(participantDetails);
      setParticipantDetailsDialogOpen(true);
    } catch (err: any) {
      showError(
        "Failed to Load Details",
        err.message ||
          "An unexpected error occurred while loading participant details",
        "View Details",
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          {/* Skeleton back button */}
          <Skeleton variant="text" width={160} height={36} sx={{ mb: 2 }} />

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Skeleton study info card */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={40}
                    sx={{ mb: 2 }}
                  />
                  <Skeleton variant="text" width="90%" height={24} />
                  <Skeleton
                    variant="text"
                    width="75%"
                    height={24}
                    sx={{ mb: 3 }}
                  />
                  <Skeleton variant="text" width="40%" height={28} />
                </CardContent>
              </Card>
            </Grid>
            {/* Skeleton action buttons */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[...Array(4)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Skeleton tabs */}
          <Paper sx={{ width: "100%", mb: 3 }}>
            <Box sx={{ px: 2, py: 1 }}>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} variant="text" width={100} height={36} />
                ))}
              </Box>
              {/* Skeleton table rows */}
              {[...Array(5)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={52}
                  sx={{ mb: 1, borderRadius: 0.5 }}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (!study) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">Study not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/researcher/dashboard")}
              sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
            >
              Back to Dashboard
            </Button>
            <LatestUpdatesCard
              variant="compact"
              maxItems={5}
              sx={{
                width: { xs: "100%", md: 360 },
              }}
            />
          </Box>

          <Grid container spacing={3}>
            {/* Left side - Study info box */}
            <Grid item xs={12} md={8}>
              <Card sx={{ height: "100%" }}>
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    {/* Line 1: Study name and status */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          hyphens: "auto",
                          flex: 1,
                          mr: 2,
                        }}
                      >
                        {study.name}
                      </Typography>
                      <Chip
                        label={study.status}
                        color={
                          study.status === "active" ? "success" : "default"
                        }
                        size="medium"
                        sx={{ flexShrink: 0 }}
                      />
                    </Box>

                    {/* Line 2: Study description */}
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.6 }}
                    >
                      {study.description || "No description provided"}
                    </Typography>
                  </Box>

                  {/* Line 3: Study code with copy icon - positioned at bottom */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: "auto",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, fontSize: "1.1rem" }}
                      >
                        Study Code:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          fontFamily: "monospace",
                          backgroundColor: "grey.100",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {study.studyCode}
                      </Typography>
                      <Tooltip
                        title={
                          copiedStudyCode === study.studyCode
                            ? "Copied!"
                            : "Copy"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={copyStudyCode}
                          sx={{ p: 0.5 }}
                        >
                          {copiedStudyCode === study.studyCode ? (
                            <CheckCircle fontSize="small" color="success" />
                          ) : (
                            <ContentCopy fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {study.duration && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, fontSize: "1.1rem" }}
                        >
                          Study Duration:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            fontSize: "1.1rem",
                            backgroundColor: "primary.50",
                            color: "primary.main",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                          }}
                        >
                          {study.duration}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right side - Action buttons */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  height: "100%",
                }}
              >
                {/* Top row: Edit, Refresh, and Check Fitbit Status */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => {
                      if (study) {
                        setEditForm({
                          name: study.name,
                          description: study.description || "",
                          duration: study.duration || "",
                        });
                      }
                      setEditDialogOpen(true);
                    }}
                    sx={{ py: 1.5, fontSize: "1rem", flex: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={loadStudyData}
                    sx={{ py: 1.5, fontSize: "1rem", flex: 1 }}
                  >
                    Refresh
                  </Button>
                </Box>

                {/* Bottom buttons: Invite, Contact, and Template Management */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Email />}
                    onClick={() => setInviteDialogOpen(true)}
                    sx={{ py: 1.5, fontSize: "0.875rem", flex: 1 }}
                  >
                    Invite Fitbit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Apple />}
                    onClick={() => setIosInviteDialogOpen(true)}
                    sx={{ py: 1.5, fontSize: "0.875rem", flex: 1 }}
                  >
                    Invite iOS
                  </Button>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  onClick={() => setContactCollaboratorDialogOpen(true)}
                  fullWidth
                  sx={{ py: 1.5, fontSize: "1rem" }}
                >
                  Invite/Contact Collaborator
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Tier-1 Metrics Summary Bar */}
        <StudyMetricsSummary
          participants={participants}
          pendingParticipants={pendingParticipants}
          loading={false}
          onMetricClick={(tabIndex) => {
            setTabValue(tabIndex);
            updateDashboardURL({ tab: tabIndex });
          }}
        />

        {/* Tabs */}
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            <Tabs
              value={activeTabGroup}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                sx: {
                  height: 3,
                  borderRadius: 999,
                },
              }}
              sx={{
                minHeight: 56,
                px: 1,
                "& .MuiTabs-flexContainer": {
                  gap: 0.5,
                },
                "& .MuiTab-root": {
                  minHeight: 56,
                  minWidth: 0,
                  px: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  color: "text.secondary",
                  alignItems: "center",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: "primary.main",
                },
              }}
            >
              <Tab
                value="participants"
                label={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    Participants
                    <ExpandMore fontSize="small" />
                  </Box>
                }
                onClick={(e) => openTabMenu("participants", e)}
              />
              <Tab
                value="data"
                label={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    Data
                    <ExpandMore fontSize="small" />
                  </Box>
                }
                onClick={(e) => openTabMenu("data", e)}
              />
              <Tab
                value="adherence"
                label={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    Adherence
                    <ExpandMore fontSize="small" />
                  </Box>
                }
                onClick={(e) => openTabMenu("adherence", e)}
              />
              <Tab
                value="study"
                data-spotlight-id="study-tab"
                label={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    Study
                    <ExpandMore fontSize="small" />
                  </Box>
                }
                onClick={(e) => openTabMenu("study", e)}
              />
              <Tab
                value="misc"
                label={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    Misc
                    <ExpandMore fontSize="small" />
                  </Box>
                }
                onClick={(e) => openTabMenu("misc", e)}
              />
              <Tab value="copilot" label="Copilot" onClick={() => selectTabValue(9)} />
            </Tabs>

            <Menu
              anchorEl={tabMenuAnchor}
              open={Boolean(tabMenuAnchor)}
              onClose={closeTabMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {tabMenuGroup === "participants" ? (
                [
                  {
                    label: "Active Participants",
                    selected:
                      tabValue === 0 && participantViewMode === "participant",
                    onClick: () => {
                      setParticipantViewMode("participant");
                      selectTabValue(0);
                    },
                  },
                  {
                    label: "Pending Participants",
                    selected:
                      tabValue === 0 && participantViewMode === "pending",
                    onClick: () => {
                      setParticipantViewMode("pending");
                      selectTabValue(0);
                    },
                  },
                  {
                    label: "Tracking ID",
                    selected: tabValue === trackingIdTabIndex,
                    onClick: () => selectTabValue(trackingIdTabIndex),
                  },
                  {
                    label: "Email History",
                    selected: tabValue === 5,
                    onClick: () => selectTabValue(5),
                  },
                ].map((item) => (
                  <MenuItem
                    key={item.label}
                    selected={item.selected}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </MenuItem>
                ))
              ) : tabMenuGroup === "data" ? (
                <>
                  <MenuItem
                    selected={tabValue === 1}
                    onClick={() => selectTabValue(1)}
                  >
                    Data Extraction
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleOpenVisualizations();
                      closeTabMenu();
                    }}
                  >
                    Visualization
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleOpenDataCoverage();
                      closeTabMenu();
                    }}
                  >
                    Data Coverage
                  </MenuItem>
                </>
              ) : tabMenuGroup === "adherence" ? (
                <>
                  <MenuItem
                    selected={tabValue === alertsTabIndex}
                    onClick={() => selectTabValue(alertsTabIndex)}
                  >
                    Alerts
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (!studyId) return;
                      window.open(
                        `/researcher/study-adherence-monitor?study=${studyId}`,
                        "_blank",
                      );
                      closeTabMenu();
                    }}
                  >
                    Study Adherence Monitor
                  </MenuItem>
                </>
              ) : tabMenuGroup === "study" ? (
                <>
                  <MenuItem
                    selected={tabValue === 6}
                    onClick={() => selectTabValue(6)}
                  >
                    Study Members
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (!studyId) return;
                      window.open(
                        `/researcher/preferences?study=${studyId}`,
                        "_blank",
                      );
                      closeTabMenu();
                    }}
                  >
                    Preferences
                  </MenuItem>
                  <MenuItem
                    data-spotlight-id="consent-management-nav"
                    onClick={() => {
                      handleOpenConsentManagement();
                      closeTabMenu();
                    }}
                  >
                    Consent Management
                  </MenuItem>
                </>
              ) : tabMenuGroup === "misc" ? (
                <MenuItem
                  selected={tabValue === 2}
                  onClick={() => selectTabValue(2)}
                >
                  Weekly Log
                </MenuItem>
              ) : null}
            </Menu>

            {/* Participants Tab */}
            <TabPanel value={tabValue} index={0}>
              {participantViewMode === "participant" ? (
                participants.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <People
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      No Participants Yet
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Send email invitations to start enrolling participants in
                      your study.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Email />}
                      onClick={() => setInviteDialogOpen(true)}
                    >
                      Send Invitations
                    </Button>
                  </Box>
                ) : (
                  <>
                    {participantsNeedingReauth.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Tooltip title="Participants needing Fitbit re-authorization for new data metrics. Click to see list and send message.">
                          <IconButton
                            color="warning"
                            onClick={(e) => setReauthAnchorEl(e.currentTarget)}
                            size="medium"
                            aria-label="Participants needing re-auth"
                          >
                            <Badge
                              badgeContent={participantsNeedingReauth.length}
                              color="error"
                            >
                              <Warning />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <Popover
                      open={Boolean(reauthAnchorEl)}
                      anchorEl={reauthAnchorEl}
                      onClose={() => setReauthAnchorEl(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <Box sx={{ p: 2, minWidth: 320 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Participants needing re-authorization
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          These participants connected Fitbit before we added
                          new data metrics. Send them a message with a re-auth
                          link.
                        </Typography>
                        <List dense disablePadding>
                          {participantsNeedingReauth.map((p) => {
                            const displayName =
                              [p.firstName, p.lastName]
                                .filter(Boolean)
                                .join(" ") ||
                              p.participantEmail ||
                              p.participantId;
                            return (
                              <ListItem
                                key={p.participantId}
                                disablePadding
                                secondaryAction={
                                  <Button
                                    size="small"
                                    startIcon={<Send />}
                                    onClick={() => handleOpenReauthSend(p)}
                                    variant="outlined"
                                  >
                                    Send message
                                  </Button>
                                }
                              >
                                <ListItemText
                                  primary={displayName}
                                  secondary={
                                    p.participantEmail &&
                                    p.participantEmail !== displayName
                                      ? p.participantEmail
                                      : undefined
                                  }
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    </Popover>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "left",
                                pb: 1,
                                width: "20%",
                              }}
                            >
                              Name
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "left",
                                pb: 1,
                                width: "20%",
                              }}
                            >
                              Email
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "left",
                                pb: 1,
                                width: "15%",
                              }}
                            >
                              Phone
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "center",
                                pb: 1,
                                width: "10%",
                              }}
                            >
                              Status
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "center",
                                pb: 1,
                                width: "10%",
                              }}
                            >
                              Data Source
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "center",
                                pb: 1,
                                width: "12%",
                              }}
                            >
                              Enrolled Date
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "center",
                                pb: 1,
                                width: "13%",
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {participants.map((participant) => {
                            const participantKey =
                              participant.id || participant.participantId;
                            const deletionJob =
                              deletionJobsByParticipantId[participantKey];
                            const historicalProgress =
                              getHistoricalProgressInfo(participant);

                            return (
                              <TableRow key={participantKey}>
                                <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.75,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {participant.firstName &&
                                      participant.lastName
                                        ? `${participant.firstName} ${participant.lastName}`
                                        : participant.firstName ||
                                          participant.lastName ||
                                          "N/A"}
                                    </Typography>
                                  </Box>
                                  {participant.participantId && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontFamily: "monospace",
                                        display: "block",
                                        mt: 0.5,
                                      }}
                                      title={participant.participantId}
                                    >
                                      ID: {participant.participantId}
                                    </Typography>
                                  )}
                                  {historicalProgress && (
                                    <Tooltip
                                      title={historicalProgress.tooltip}
                                      arrow
                                    >
                                      <Box sx={{ mt: 1.25, maxWidth: 180 }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            color={
                                              historicalProgress.kind ===
                                              "failed"
                                                ? "error.main"
                                                : "text.secondary"
                                            }
                                            sx={{ fontWeight: 600 }}
                                          >
                                            {historicalProgress.label}
                                          </Typography>
                                        </Box>
                                        <LinearProgress
                                          variant="determinate"
                                          value={historicalProgress.percentage}
                                          color={
                                            historicalProgress.kind === "failed"
                                              ? "error"
                                              : participant.historicalExtraction
                                                    ?.status === "completed"
                                                ? "success"
                                                : "primary"
                                          }
                                          sx={{
                                            height: 6,
                                            borderRadius: 999,
                                            backgroundColor: "action.hover",
                                          }}
                                        />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {participant.participantEmail || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {participant.phoneNumber || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ py: 1.5, textAlign: "center" }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Chip
                                      label={
                                        deletingParticipantIds.includes(
                                          participantKey,
                                        )
                                          ? "deleting"
                                          : getParticipantStatus(participant)
                                      }
                                      color={
                                        deletingParticipantIds.includes(
                                          participantKey,
                                        )
                                          ? "default"
                                          : getParticipantStatusColor(
                                              getParticipantStatus(participant),
                                            )
                                      }
                                      size="small"
                                    />
                                    {deletingParticipantIds.includes(
                                      participantKey,
                                    ) &&
                                      deletionJob?.progressMessage && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block",
                                            mt: 0.5,
                                            maxWidth: 180,
                                            lineHeight: 1.2,
                                            textAlign: "center",
                                          }}
                                        >
                                          {deletionJob.progressMessage}
                                        </Typography>
                                      )}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{ py: 1.5, textAlign: "center" }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {participant.fitbitTokens?.accessToken
                                      ? "Fitbit"
                                      : participant.healthkit?.authorized
                                        ? "HealthKit"
                                        : "None"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ py: 1.5, textAlign: "center" }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {participant.enrolledAt
                                      ? new Date(
                                          participant.enrolledAt,
                                        ).toLocaleDateString()
                                      : "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ py: 1.5, textAlign: "center" }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        openEditParticipantDialog(participant)
                                      }
                                      title="Edit Participant Name"
                                    >
                                      <Edit />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() =>
                                        openParticipantDetailsDialog(
                                          participant,
                                        )
                                      }
                                      title="View Details"
                                    >
                                      <Visibility />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        openDeleteDialog(participant.id)
                                      }
                                      title="Delete"
                                      disabled={deletingParticipantIds.includes(
                                        participantKey,
                                      )}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )
              ) : (
                <Box sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Participants are added here when you send invitations
                    </Typography>
                  </Box>

                  {pendingParticipants.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <People
                        sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                      />
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        No Pending Participants
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        Send email invitations to add participants to the study
                        roster.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Email />}
                        onClick={() => setInviteDialogOpen(true)}
                      >
                        Send Invitations
                      </Button>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "left",
                                pb: 2,
                                width: "60%",
                              }}
                            >
                              Participant ID
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "center",
                                pb: 2,
                              }}
                            >
                              Status
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "center",
                                pb: 2,
                              }}
                            >
                              Added Date
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                borderBottom: "2px solid",
                                borderBottomColor: "primary.main",
                                textAlign: "center",
                                pb: 2,
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingParticipants.map((participant) => (
                            <TableRow key={participant.id}>
                              <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  title={participant.id}
                                  sx={{
                                    fontFamily: "monospace",
                                    fontWeight: 500,
                                  }}
                                >
                                  {participant.id}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1.5, textAlign: "center" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Chip
                                    label={participant.status}
                                    color={
                                      participant.status === "pre-enrolled"
                                        ? "info"
                                        : participant.status === "invited"
                                          ? "warning"
                                          : "error"
                                    }
                                    size="small"
                                  />
                                </Box>
                              </TableCell>
                              <TableCell sx={{ py: 1.5, textAlign: "center" }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {participant.createdAt
                                    ? new Date(
                                        participant.createdAt,
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1.5, textAlign: "center" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 1,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      openEditPendingDialog(participant)
                                    }
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      openDeletePendingDialog(
                                        participant.participantId,
                                      )
                                    }
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </TabPanel>

            {/* Study Members Tab */}
            <TabPanel value={tabValue} index={6}>
              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    All researchers involved in this study
                  </Typography>
                </Box>

                {studyMembers.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <People
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      No Study Members
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Study members will appear here once collaborators join the
                      study.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    {/* Main Researcher Section */}
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
                      >
                        Main Researcher
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  borderBottom: "2px solid",
                                  borderBottomColor: "primary.main",
                                  textAlign: "left",
                                  pb: 1,
                                  width: "20%",
                                }}
                              >
                                Name
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  borderBottom: "2px solid",
                                  borderBottomColor: "primary.main",
                                  textAlign: "left",
                                  pb: 1,
                                  width: "25%",
                                }}
                              >
                                Email
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  borderBottom: "2px solid",
                                  borderBottomColor: "primary.main",
                                  textAlign: "center",
                                  pb: 1,
                                  width: "15%",
                                }}
                              >
                                Status
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  borderBottom: "2px solid",
                                  borderBottomColor: "primary.main",
                                  textAlign: "center",
                                  pb: 1,
                                  width: "15%",
                                }}
                              >
                                Joined Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  borderBottom: "2px solid",
                                  borderBottomColor: "primary.main",
                                  textAlign: "center",
                                  pb: 1,
                                  width: "10%",
                                }}
                              >
                                Role
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {studyMembers
                              .filter((member) => member.role === "main")
                              .map((member) => {
                                return (
                                  <TableRow key={member.id}>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "left" }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                        title={member.name}
                                      >
                                        {member.name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "left" }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        title={member.email}
                                      >
                                        {member.email}
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "center" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Chip
                                          label={member.status}
                                          color={
                                            member.status === "active"
                                              ? "success"
                                              : "warning"
                                          }
                                          size="small"
                                        />
                                      </Box>
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "center" }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {member.joinedDate
                                          ? new Date(
                                              member.joinedDate,
                                            ).toLocaleDateString()
                                          : "N/A"}
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "center" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Chip
                                          label="Main"
                                          color="primary"
                                          size="small"
                                          sx={{ fontWeight: 600 }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    {/* Collaborative Researchers Section */}
                    {studyMembers.filter((member) => member.role === "collab")
                      .length > 0 && (
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, color: "info.main", fontWeight: 600 }}
                        >
                          Collaborative Researchers (
                          {
                            studyMembers.filter(
                              (member) => member.role === "collab",
                            ).length
                          }
                          )
                        </Typography>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    borderBottom: "2px solid",
                                    borderBottomColor: "info.main",
                                    textAlign: "left",
                                    pb: 1,
                                    width: "20%",
                                  }}
                                >
                                  Name
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    borderBottom: "2px solid",
                                    borderBottomColor: "info.main",
                                    textAlign: "left",
                                    pb: 1,
                                    width: "25%",
                                  }}
                                >
                                  Email
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    borderBottom: "2px solid",
                                    borderBottomColor: "info.main",
                                    textAlign: "center",
                                    pb: 1,
                                    width: "15%",
                                  }}
                                >
                                  Status
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    borderBottom: "2px solid",
                                    borderBottomColor: "info.main",
                                    textAlign: "center",
                                    pb: 1,
                                    width: "15%",
                                  }}
                                >
                                  Joined Date
                                </TableCell>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    borderBottom: "2px solid",
                                    borderBottomColor: "info.main",
                                    textAlign: "center",
                                    pb: 1,
                                    width: "10%",
                                  }}
                                >
                                  Role
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {studyMembers
                                .filter((member) => member.role === "collab")
                                .map((member) => (
                                  <TableRow key={member.id}>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "left" }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                        title={member.name}
                                      >
                                        {member.name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "left" }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        title={member.email}
                                      >
                                        {member.email}
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "center" }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Chip
                                          label={member.status}
                                          color={
                                            member.status === "active"
                                              ? "success"
                                              : "warning"
                                          }
                                          size="small"
                                        />
                                      </Box>
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "center" }}
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {member.joinedDate
                                          ? new Date(
                                              member.joinedDate,
                                            ).toLocaleDateString()
                                          : "N/A"}
                                      </Typography>
                                    </TableCell>
                                    <TableCell
                                      sx={{ py: 1.5, textAlign: "center" }}
                                    >
                                      <Chip
                                        label="Collaborator"
                                        color="info"
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Email History Tab */}
            <TabPanel value={tabValue} index={5}>
              {emailHistory.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Email
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    No Emails Sent Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email history will appear here once you send invitations to
                    participants.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ px: 2 }}>
                  {emailHistory.map((email, index) => {
                    // Classify based on emailMode and invitationType (not guessing from content)
                    return (
                      <React.Fragment key={index}>
                        <Box
                          sx={{
                            py: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {email.emailSubject}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {email.emailMode === "invite"
                                ? "Invited participants"
                                : "Contacted participants"}{" "}
                              ({email.sentCount}){" • "}
                              {new Date(email.sentAt).toLocaleString()}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedEmail(email);
                              setEmailViewOpen(true);
                            }}
                            sx={{ ml: 2, flexShrink: 0 }}
                          >
                            View
                          </Button>
                        </Box>
                        {index < emailHistory.length - 1 && (
                          <Divider sx={{ mx: -2 }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              )}
            </TabPanel>

            {/* Templates moved to standalone page */}

            {/* Data Extraction Tab */}
            <TabPanel value={tabValue} index={1}>
              <DataExtractionInterface
                studyId={studyId!}
                studyName={study?.name || "Study"}
                participants={participants.map((p) => ({
                  id: p.id,
                  participantId: p.participantId,
                  participantEmail: p.participantEmail,
                  firstName: p.firstName,
                  lastName: p.lastName,
                  phoneNumber: p.phoneNumber,
                  authorizationStatus: p.authorizationStatus || false,
                  hasTokens: !!(p.fitbitTokens?.accessToken || (p as any).fitbitData?.accessToken || (p as any).fitbitAccessToken),
                  fitbitTokens: p.fitbitTokens || ((p as any).fitbitData ? {
                    accessToken: (p as any).fitbitData?.accessToken,
                    expiresAt: (p as any).fitbitData?.expiresAt,
                  } : undefined),
                  fitbitData: (p as any).fitbitData,
                  fitbitAccessToken: (p as any).fitbitAccessToken,
                  fitbitUserId: p.fitbitUserId,
                  healthkit: p.healthkit,
                  enrolledAt: p.enrolledAt,
                  historicalExtraction: p.historicalExtraction,
                }))}
                onRefreshParticipants={loadStudyData}
              />
            </TabPanel>

            {/* Weekly Log Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6">Weekly Log</Typography>
                <WeeklyLogPanel
                  studyId={studyId!}
                  researcherId={user?.uid || null}
                  showSuccess={showSuccess}
                  showError={showError}
                />
              </Box>
            </TabPanel>

            {/* Alerts Tab */}
            <TabPanel value={tabValue} index={alertsTabIndex}>
              <Box sx={{ p: 2 }}>
                <AlertsTab studyId={studyId!} />
              </Box>
            </TabPanel>

            {/* Tracking ID Tab */}
            <TabPanel value={tabValue} index={trackingIdTabIndex}>
              <TrackingIdTab
                participants={participants}
                studyId={studyId || ""}
                onRefresh={loadStudyData}
              />
            </TabPanel>
            <KeepMountedTabPanel value={tabValue} index={9}>
              <CopilotTab studyId={studyId || ""} />
            </KeepMountedTabPanel>
          </Paper>
        </Box>

        {/* Invite/Contact Participants Dialog */}
        <Dialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                {participantEmailMode === "invite"
                  ? "Invite Participants"
                  : "Contact Participants"}
              </Typography>
              <ToggleButtonGroup
                value={participantEmailMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setParticipantEmailMode(newMode);
                  }
                }}
                size="small"
              >
                <ToggleButton value="invite">Invite</ToggleButton>
                <ToggleButton value="contact">Contact</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {participantEmailMode === "invite"
                ? `Send personalized invitations to participants. They will be added to the study roster and can enroll using the study code. Study Code: ${study.studyCode}`
                : "Send an email to study participants. You can select existing participants or enter email addresses manually."}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {participantEmailMode === "invite" ? (
                <>
                  <TextField
                    label="Study Name"
                    fullWidth
                    variant="outlined"
                    value={inviteForm.studyName}
                    onChange={(e) =>
                      setInviteForm((prev) => ({
                        ...prev,
                        studyName: e.target.value,
                      }))
                    }
                    placeholder={study.name}
                    helperText="Leave blank to use default study name"
                  />

                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    value={inviteForm.description}
                    onChange={(e) =>
                      setInviteForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder={study.description || "Enter study description"}
                    helperText="Leave blank to use default study description"
                  />

                  <TextField
                    label="Duration"
                    fullWidth
                    variant="outlined"
                    value={inviteForm.duration}
                    onChange={(e) =>
                      setInviteForm((prev) => ({
                        ...prev,
                        duration: e.target.value,
                      }))
                    }
                    placeholder="e.g., 4 weeks, 2 months"
                  />
                </>
              ) : (
                <>
                  <TextField
                    label="Email Title"
                    fullWidth
                    variant="outlined"
                    value={contactForm.emailTitle}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        emailTitle: e.target.value,
                      }))
                    }
                    placeholder="Enter email subject"
                    required
                  />

                  <TextField
                    label="Email Content"
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    value={contactForm.content}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        content: e.target.value,
                      }))
                    }
                    placeholder="Enter your message to participants..."
                    required
                  />
                </>
              )}

              {participantEmailMode === "invite" ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Add Participants
                  </Typography>
                  {inviteForm.participants.map((participant, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      {index === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          * At least one participant is required
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          fullWidth
                          label="Email Address"
                          value={participant.email}
                          onChange={(e) => {
                            const newParticipants = [
                              ...inviteForm.participants,
                            ];
                            newParticipants[index].email = e.target.value;
                            setInviteForm((prev) => ({
                              ...prev,
                              participants: newParticipants,
                            }));
                          }}
                          placeholder="participant@example.com"
                          required
                          type="email"
                          autoFocus={index === 0}
                          variant="outlined"
                          sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Phone Number (Optional)"
                          value={participant.phoneNumber}
                          onChange={(e) => {
                            const newParticipants = [
                              ...inviteForm.participants,
                            ];
                            newParticipants[index].phoneNumber =
                              formatPhoneNumber(e.target.value);
                            setInviteForm((prev) => ({
                              ...prev,
                              participants: newParticipants,
                            }));
                          }}
                          placeholder="(000)-0000-000"
                          variant="outlined"
                          sx={{
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                        {/* Delete button or Required badge */}
                        {inviteForm.participants.length > 1 && index > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: "56px",
                              width: "110px",
                              cursor: "pointer",
                              flexShrink: 0,
                              "&:hover": {
                                opacity: 0.7,
                              },
                              transition: "opacity 0.2s",
                            }}
                            onClick={() => {
                              const newParticipants =
                                inviteForm.participants.filter(
                                  (_, i) => i !== index,
                                );
                              setInviteForm((prev) => ({
                                ...prev,
                                participants: newParticipants,
                              }));
                            }}
                            title="Remove participant"
                          >
                            <Delete
                              sx={{ color: "error.main", fontSize: "1.5rem" }}
                            />
                          </Box>
                        ) : null}
                        {index === 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 1.5,
                              py: 1,
                              backgroundColor: "primary.light",
                              borderRadius: 1,
                              height: "56px",
                              width: "110px",
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="primary.contrastText"
                              sx={{ fontWeight: 600 }}
                            >
                              Required
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<People />}
                    onClick={() => {
                      setInviteForm((prev) => ({
                        ...prev,
                        participants: [
                          ...prev.participants,
                          { email: "", phoneNumber: "" },
                        ],
                      }));
                    }}
                    sx={{ mb: 2 }}
                  >
                    Add Another Participant
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Recipients
                  </Typography>

                  {participants.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Select from existing participants:
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: 200,
                          overflowY: "auto",
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        {participants.map((participant) => (
                          <Box
                            key={participant.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              py: 0.5,
                            }}
                          >
                            <Checkbox
                              checked={contactForm.recipients.includes(
                                participant.participantId,
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setContactForm((prev) => ({
                                    ...prev,
                                    recipients: [
                                      ...prev.recipients,
                                      participant.participantId,
                                    ],
                                  }));
                                } else {
                                  setContactForm((prev) => ({
                                    ...prev,
                                    recipients: prev.recipients.filter(
                                      (id) => id !== participant.participantId,
                                    ),
                                  }));
                                }
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "monospace" }}
                            >
                              {participant.participantId}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={contactForm.recipients.filter(
                      (recipient) =>
                        !participants.some(
                          (p) => p.participantId === recipient,
                        ),
                    )}
                    onChange={(event, newValue) => {
                      const validEmails = newValue.filter((email: string) => {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        return emailRegex.test(email);
                      });
                      const existingParticipants =
                        contactForm.recipients.filter((recipient) =>
                          participants.some(
                            (p) => p.participantId === recipient,
                          ),
                        );
                      setContactForm((prev) => ({
                        ...prev,
                        recipients: [...existingParticipants, ...validEmails],
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Additional Email Addresses"
                        placeholder="Enter additional email addresses (press Enter after each)"
                        helperText="Enter valid email addresses for non-participants"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          key={index}
                        />
                      ))
                    }
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (participantEmailMode === "invite") {
                  // Populate review form with generated content
                  const emailTitle = `Invitation to participate in ${
                    inviteForm.studyName || study?.name
                  }`;
                  const emailContent = `Dear Participant,

You are invited to participate in our research study: ${
                    inviteForm.studyName || study?.name
                  }

${inviteForm.description || study?.description || "No description provided"}

Duration: ${inviteForm.duration || "Not specified"}

To participate, please click the link below and follow the instructions to connect your Fitbit account.

Study Code: ${study?.studyCode}

Thank you for your interest in participating in our research.

Best regards,
Research Team`;

                  setInviteReviewForm({
                    emailTitle,
                    content: emailContent,
                  });
                  setInviteReviewOpen(true);
                } else {
                  // Contact mode - populate review form with contact form content
                  setContactReviewForm({
                    emailTitle: contactForm.emailTitle,
                    content: contactForm.content,
                  });
                  setContactReviewOpen(true);
                }
              }}
              variant="contained"
              disabled={
                participantEmailMode === "invite"
                  ? inviteForm.participants.length === 0 ||
                    !inviteForm.participants.some((p) => p.email.trim())
                  : !contactForm.emailTitle.trim() ||
                    !contactForm.content.trim() ||
                    contactForm.recipients.length === 0
              }
            >
              Continue to Review
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite iOS Participants Dialog */}
        <Dialog
          open={iosInviteDialogOpen}
          onClose={() => {
            setIosInviteDialogOpen(false);
            setIosInviteMode("qr");
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                {iosInviteMode === "contact" ? "Contact iOS" : "Invite iOS"}
              </Typography>
              <ToggleButtonGroup
                value={iosInviteMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setIosInviteMode(newMode);
                  }
                }}
                size="small"
              >
                <ToggleButton value="qr">QR Code</ToggleButton>
                <ToggleButton value="invite">Invite</ToggleButton>
                <ToggleButton value="contact">Contact</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogTitle>
          <DialogContent>
            {iosInviteMode === "qr" && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Share this QR code or link with participants to enroll via the
                  iOS app. This is for Apple Health (HealthKit) data collection
                  only.
                </Typography>
                {study ? (
                  <HealthKitQRGenerator
                    studyCode={study.studyCode}
                    studyName={study.name}
                  />
                ) : (
                  <Alert severity="info">
                    Study details are still loading. Refresh and try again.
                  </Alert>
                )}
              </>
            )}

            {iosInviteMode === "invite" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Send email invitations to iOS participants. They will receive
                  a link to download the StudySync app and enroll with study
                  code {study?.studyCode}.
                </Typography>
                {renderParticipantInviteForm(iosInviteForm, setIosInviteForm)}
              </Box>
            )}

            {iosInviteMode === "contact" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Send contact emails to iOS participants. You can select
                  existing participants or enter additional email addresses
                  manually.
                </Typography>
                {renderParticipantContactForm(
                  iosContactForm,
                  setIosContactForm,
                )}
              </Box>
            )}
          </DialogContent>
          {iosInviteMode !== "qr" && (
            <DialogActions>
              <Button
                onClick={() => {
                  setIosInviteDialogOpen(false);
                  setIosInviteMode("qr");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (iosInviteMode === "invite") {
                    const emailTitle = `Invitation to participate in ${
                      iosInviteForm.studyName || study?.name
                    }`;
                    const emailContent = `Dear Participant,

You are invited to participate in our research study: ${
                      iosInviteForm.studyName || study?.name
                    }

${iosInviteForm.description || study?.description || "No description provided"}

Duration: ${iosInviteForm.duration || "Not specified"}

To participate, please download the StudySync iOS app and use the link below to enroll with your Apple Health data.

Study Code: ${study?.studyCode}

Thank you for your interest in participating in our research.

Best regards,
Research Team`;

                    setIosInviteReviewForm({
                      emailTitle,
                      content: emailContent,
                    });
                    setIosInviteReviewOpen(true);
                  } else {
                    setIosContactReviewForm({
                      emailTitle: iosContactForm.emailTitle,
                      content: iosContactForm.content,
                    });
                    setIosContactReviewOpen(true);
                  }
                }}
                variant="contained"
                disabled={
                  iosInviteMode === "invite"
                    ? iosInviteForm.participants.length === 0 ||
                      !iosInviteForm.participants.some((p) => p.email.trim())
                    : !iosContactForm.emailTitle.trim() ||
                      !iosContactForm.content.trim() ||
                      iosContactForm.recipients.length === 0
                }
              >
                Continue to Review
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* iOS Email Invitation Review Dialog */}
        <Dialog
          open={iosInviteReviewOpen}
          onClose={() => setIosInviteReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review iOS Invitation Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit the email before sending. The enrollment deep link
              will be appended automatically by the server.
            </Typography>
            <TextField
              label="Email Subject"
              fullWidth
              variant="outlined"
              value={iosInviteReviewForm.emailTitle}
              onChange={(e) =>
                setIosInviteReviewForm((prev) => ({
                  ...prev,
                  emailTitle: e.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email Content"
              fullWidth
              multiline
              rows={10}
              variant="outlined"
              value={iosInviteReviewForm.content}
              onChange={(e) =>
                setIosInviteReviewForm((prev) => ({
                  ...prev,
                  content: e.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIosInviteReviewOpen(false)}>Back</Button>
            <Button
              onClick={() => {
                handleSendIosInvitations();
              }}
              variant="contained"
              disabled={
                !iosInviteReviewForm.emailTitle.trim() ||
                !iosInviteReviewForm.content.trim()
              }
            >
              Send Invitations
            </Button>
          </DialogActions>
        </Dialog>

        {/* iOS Contact Email Review Dialog */}
        <Dialog
          open={iosContactReviewOpen}
          onClose={() => setIosContactReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review iOS Contact Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit the email before sending.
            </Typography>
            <TextField
              label="Email Subject"
              fullWidth
              variant="outlined"
              value={iosContactReviewForm.emailTitle}
              onChange={(e) =>
                setIosContactReviewForm((prev) => ({
                  ...prev,
                  emailTitle: e.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email Content"
              fullWidth
              multiline
              rows={10}
              variant="outlined"
              value={iosContactReviewForm.content}
              onChange={(e) =>
                setIosContactReviewForm((prev) => ({
                  ...prev,
                  content: e.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIosContactReviewOpen(false)}>Back</Button>
            <Button
              onClick={() => {
                handleContactIosParticipants();
              }}
              variant="contained"
              disabled={
                !iosContactReviewForm.emailTitle.trim() ||
                !iosContactReviewForm.content.trim()
              }
            >
              Send Email
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contact Participants Dialog */}
        <Dialog
          open={contactDialogOpen}
          onClose={() => setContactDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Contact Participants</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Send an email to study participants. You can select existing
              participants or enter email addresses manually.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Title"
                fullWidth
                variant="outlined"
                value={contactForm.emailTitle}
                onChange={(e) =>
                  setContactForm((prev) => ({
                    ...prev,
                    emailTitle: e.target.value,
                  }))
                }
                placeholder="Enter email subject"
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                value={contactForm.content}
                onChange={(e) =>
                  setContactForm((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                placeholder="Enter your message to participants..."
                required
              />

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Recipients
              </Typography>

              {participants.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Select from existing participants:
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {participants.map((participant) => (
                      <Box
                        key={participant.id}
                        sx={{ display: "flex", alignItems: "center", py: 0.5 }}
                      >
                        <Checkbox
                          checked={contactForm.recipients.includes(
                            participant.participantEmail,
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setContactForm((prev) => ({
                                ...prev,
                                recipients: [
                                  ...prev.recipients,
                                  participant.participantEmail,
                                ],
                              }));
                            } else {
                              setContactForm((prev) => ({
                                ...prev,
                                recipients: prev.recipients.filter(
                                  (email) =>
                                    email !== participant.participantEmail,
                                ),
                              }));
                            }
                          }}
                        />
                        <Typography variant="body2">
                          {participant.participantEmail}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={contactForm.recipients.filter(
                  (email) =>
                    !participants.some((p) => p.participantEmail === email),
                )}
                onChange={(event, newValue) => {
                  const validEmails = newValue.filter((email: string) => {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emailRegex.test(email);
                  });
                  const existingParticipants = contactForm.recipients.filter(
                    (email) =>
                      participants.some((p) => p.participantEmail === email),
                  );
                  setContactForm((prev) => ({
                    ...prev,
                    recipients: [...existingParticipants, ...validEmails],
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Additional Email Addresses"
                    placeholder="Enter additional email addresses (press Enter after each)"
                    helperText="Enter valid email addresses for non-participants"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={index}
                    />
                  ))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                // Populate review form with contact form content
                setContactReviewForm({
                  emailTitle: contactForm.emailTitle,
                  content: contactForm.content,
                });
                setContactReviewOpen(true);
              }}
              variant="contained"
              disabled={
                !contactForm.emailTitle.trim() || !contactForm.content.trim()
              }
            >
              Continue to Review
            </Button>
          </DialogActions>
        </Dialog>

        {/* Send re-auth message to participant */}
        <Dialog
          open={reauthSendDialogOpen}
          onClose={() => {
            if (!reauthSending) {
              setReauthSendDialogOpen(false);
              setReauthSendParticipant(null);
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Send re-auth message
            {reauthSendParticipant && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                sx={{ display: "block", fontWeight: "normal", mt: 0.5 }}
              >
                To: {reauthSendParticipant.participantEmail}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Subject"
              fullWidth
              variant="outlined"
              value={reauthEmailSubject}
              onChange={(e) => setReauthEmailSubject(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={8}
              variant="outlined"
              value={reauthEmailBody}
              onChange={(e) => setReauthEmailBody(e.target.value)}
              helperText="The re-auth link is included above. You can edit the message before sending."
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setReauthSendDialogOpen(false);
                setReauthSendParticipant(null);
              }}
              disabled={reauthSending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendReauthMessage}
              disabled={
                reauthSending ||
                !reauthEmailSubject.trim() ||
                !reauthEmailBody.trim()
              }
              startIcon={reauthSending ? undefined : <Send />}
            >
              {reauthSending ? "Sending…" : "Send message"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite/Contact Collaborator Dialog */}
        <Dialog
          open={contactCollaboratorDialogOpen}
          onClose={() => {
            setContactCollaboratorDialogOpen(false);
            // Reset forms when dialog is closed
            setInviteCollaboratorForm({
              emailTitle: "",
              content: "",
              recipients: [],
            });
            setContactCollaboratorForm({
              emailTitle: "",
              content: "",
              recipients: [],
            });
            setCollaboratorEmailMode("invite"); // Reset to invite mode
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                {collaboratorEmailMode === "invite"
                  ? "Invite Collaborators"
                  : "Contact Collaborators"}
              </Typography>
              <ToggleButtonGroup
                value={collaboratorEmailMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setCollaboratorEmailMode(newMode);
                  }
                }}
                size="small"
              >
                <ToggleButton value="invite">Invite</ToggleButton>
                <ToggleButton value="contact">Contact</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {collaboratorEmailMode === "invite"
                ? "Invite new collaborators to join this study. They will receive an invitation email with pre-filled study code and password for easy joining."
                : "Send an email to existing study collaborators. You can enter collaborator email addresses manually."}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Title"
                fullWidth
                variant="outlined"
                value={
                  collaboratorEmailMode === "invite"
                    ? inviteCollaboratorForm.emailTitle
                    : contactCollaboratorForm.emailTitle
                }
                onChange={(e) => {
                  if (collaboratorEmailMode === "invite") {
                    setInviteCollaboratorForm((prev) => ({
                      ...prev,
                      emailTitle: e.target.value,
                    }));
                  } else {
                    setContactCollaboratorForm((prev) => ({
                      ...prev,
                      emailTitle: e.target.value,
                    }));
                  }
                }}
                placeholder={
                  collaboratorEmailMode === "invite"
                    ? "Enter invitation subject"
                    : "Enter email subject"
                }
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                value={
                  collaboratorEmailMode === "invite"
                    ? inviteCollaboratorForm.content
                    : contactCollaboratorForm.content
                }
                onChange={(e) => {
                  if (collaboratorEmailMode === "invite") {
                    setInviteCollaboratorForm((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }));
                  } else {
                    setContactCollaboratorForm((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }));
                  }
                }}
                placeholder={
                  collaboratorEmailMode === "invite"
                    ? "Enter your invitation message..."
                    : "Enter your message to collaborators..."
                }
                required
              />

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Recipients
              </Typography>

              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={
                  collaboratorEmailMode === "invite"
                    ? inviteCollaboratorForm.recipients
                    : contactCollaboratorForm.recipients
                }
                onChange={(event, newValue) => {
                  const validEmails = newValue.filter((email: string) => {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emailRegex.test(email);
                  });
                  if (collaboratorEmailMode === "invite") {
                    setInviteCollaboratorForm((prev) => ({
                      ...prev,
                      recipients: validEmails,
                    }));
                  } else {
                    setContactCollaboratorForm((prev) => ({
                      ...prev,
                      recipients: validEmails,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      collaboratorEmailMode === "invite"
                        ? "Collaborator Email Addresses to Invite"
                        : "Collaborator Email Addresses"
                    }
                    placeholder="Enter email addresses (press Enter after each)"
                    helperText="Enter valid email addresses like collaborator@example.com"
                    required
                    autoFocus
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={index}
                    />
                  ))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactCollaboratorDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (collaboratorEmailMode === "invite") {
                  // Populate review form with invite collaborator form content
                  // If no custom content, use default template
                  const defaultContent = inviteCollaboratorForm.content.trim()
                    ? inviteCollaboratorForm.content
                    : `Dear Colleague,\n\nYou have been invited to collaborate on the research study: ${
                        study?.name || "this study"
                      }\n\nBest regards,\nThe Research Team`;

                  setInviteCollaboratorReviewForm({
                    emailTitle:
                      inviteCollaboratorForm.emailTitle ||
                      `Collaborator Invitation: ${study?.name || "Study"}`,
                    content: defaultContent,
                  });
                  setInviteCollaboratorReviewOpen(true);
                } else {
                  // Populate review form with contact collaborator form content
                  setContactCollaboratorReviewForm({
                    emailTitle: contactCollaboratorForm.emailTitle,
                    content: contactCollaboratorForm.content,
                  });
                  setContactCollaboratorReviewOpen(true);
                }
              }}
              variant="contained"
              disabled={
                collaboratorEmailMode === "invite"
                  ? !inviteCollaboratorForm.emailTitle.trim() ||
                    !inviteCollaboratorForm.content.trim()
                  : !contactCollaboratorForm.emailTitle.trim() ||
                    !contactCollaboratorForm.content.trim()
              }
            >
              Continue to Review
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite Review Dialog */}
        <Dialog
          open={inviteReviewOpen}
          onClose={() => {
            if (!inviteSending) setInviteReviewOpen(false);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {inviteSending ? "Sending Invitation Email" : "Review & Edit Invitation Email"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {inviteSending
                ? "Please keep this window open while StudySync sends the email and updates the study roster."
                : "Review and edit the invitation email before sending. You can modify both the title and content."}
            </Typography>

            {inviteSending && (
              <Alert
                severity="info"
                icon={<CircularProgress size={20} />}
                sx={{ mb: 3 }}
              >
                Sending invitation email. This may take a moment while the backend processes the request.
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Subject"
                fullWidth
                variant="outlined"
                value={inviteReviewForm.emailTitle}
                onChange={(e) =>
                  setInviteReviewForm((prev) => ({
                    ...prev,
                    emailTitle: e.target.value,
                  }))
                }
                disabled={inviteSending}
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                value={inviteReviewForm.content}
                onChange={(e) =>
                  setInviteReviewForm((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                disabled={inviteSending}
                required
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Recipients:{" "}
              {inviteForm.participants
                .filter((p) => p.email.trim())
                .map((p) => p.email)
                .join(", ")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setInviteReviewOpen(false)}
              disabled={inviteSending}
            >
              Back to Edit
            </Button>
            <Button
              onClick={handleSendInvitations}
              variant="contained"
              disabled={
                inviteSending ||
                !inviteReviewForm.emailTitle.trim() ||
                !inviteReviewForm.content.trim()
              }
              startIcon={inviteSending ? <CircularProgress size={18} color="inherit" /> : <Send />}
            >
              {inviteSending ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contact Review Dialog */}
        <Dialog
          open={contactReviewOpen}
          onClose={() => setContactReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review & Edit Contact Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review and edit the contact email before sending. You can modify
              both the title and content.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Subject"
                fullWidth
                variant="outlined"
                value={contactReviewForm.emailTitle}
                onChange={(e) =>
                  setContactReviewForm((prev) => ({
                    ...prev,
                    emailTitle: e.target.value,
                  }))
                }
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                value={contactReviewForm.content}
                onChange={(e) =>
                  setContactReviewForm((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                required
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Recipients: {contactForm.recipients.join(", ")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactReviewOpen(false)}>
              Back to Edit
            </Button>
            <Button
              onClick={() => {
                setContactReviewOpen(false);
                handleContactParticipants();
              }}
              variant="contained"
              disabled={
                !contactReviewForm.emailTitle.trim() ||
                !contactReviewForm.content.trim()
              }
            >
              Send Email
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite Collaborator Review Dialog */}
        <Dialog
          open={inviteCollaboratorReviewOpen}
          onClose={() => setInviteCollaboratorReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review & Edit Collaborator Invitation Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review and edit the collaborator invitation email before sending.
              The study code and password will be automatically included in the
              email. Collaborators will be instructed to use the research
              dashboard to join the study.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Title"
                fullWidth
                variant="outlined"
                value={inviteCollaboratorReviewForm.emailTitle}
                onChange={(e) =>
                  setInviteCollaboratorReviewForm((prev) => ({
                    ...prev,
                    emailTitle: e.target.value,
                  }))
                }
                placeholder="Enter invitation subject"
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={8}
                variant="outlined"
                value={inviteCollaboratorReviewForm.content}
                onChange={(e) =>
                  setInviteCollaboratorReviewForm((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                placeholder="Enter your invitation message..."
                required
              />

              {/* Study Information Display */}
              {study && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Study Information (will be included in email):
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Study Name:</strong> {study.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Study Code:</strong> {study.studyCode}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Study Password:</strong>{" "}
                    {study.password || "No password set"}
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Recipients: {inviteCollaboratorForm.recipients.join(", ")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteCollaboratorReviewOpen(false)}>
              Back to Edit
            </Button>
            <Button
              onClick={() => {
                setInviteCollaboratorReviewOpen(false);
                handleInviteCollaborator();
              }}
              variant="contained"
              disabled={
                !inviteCollaboratorReviewForm.emailTitle.trim() ||
                !inviteCollaboratorReviewForm.content.trim()
              }
            >
              Send Invitation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contact Collaborator Review Dialog */}
        <Dialog
          open={contactCollaboratorReviewOpen}
          onClose={() => setContactCollaboratorReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review & Edit Collaborator Contact Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review and edit the collaborator contact email before sending. You
              can modify both the title and content.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Subject"
                fullWidth
                variant="outlined"
                value={contactCollaboratorReviewForm.emailTitle}
                onChange={(e) =>
                  setContactCollaboratorReviewForm((prev) => ({
                    ...prev,
                    emailTitle: e.target.value,
                  }))
                }
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                value={contactCollaboratorReviewForm.content}
                onChange={(e) =>
                  setContactCollaboratorReviewForm((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                required
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Recipients: {contactCollaboratorForm.recipients.join(", ")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactCollaboratorReviewOpen(false)}>
              Back to Edit
            </Button>
            <Button
              onClick={() => {
                setContactCollaboratorReviewOpen(false);
                handleContactCollaborator();
              }}
              variant="contained"
              disabled={
                !contactCollaboratorReviewForm.emailTitle.trim() ||
                !contactCollaboratorReviewForm.content.trim()
              }
            >
              Send Email
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email View Dialog */}
        <Dialog
          open={emailViewOpen}
          onClose={() => setEmailViewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Email Details</DialogTitle>
          <DialogContent>
            {selectedEmail && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Box 1: Email Title */}
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedEmail.emailSubject}
                  </Typography>
                </Paper>

                {/* Line 2: Recipients */}
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}
                  >
                    RECIPIENTS ({selectedEmail.sentCount})
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                      wordBreak: "break-all",
                    }}
                  >
                    {selectedEmail.participants &&
                    selectedEmail.participants.length > 0
                      ? selectedEmail.participants
                          .map((p: any) => p.email || p.participantEmail)
                          .join(", ")
                      : `${selectedEmail.sentCount} recipients`}
                  </Typography>
                </Paper>

                {/* Box 3: Email Content */}
                <Paper
                  sx={{
                    p: 3,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                  >
                    {selectedEmail.emailBody || "Email content not available"}
                  </Typography>
                </Paper>

                {/* Additional Info */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Sent on {new Date(selectedEmail.sentAt).toLocaleString()} •
                  Status: {selectedEmail.deliveryStatus}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailViewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Study Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Study</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Update study information and settings.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Study Name"
                fullWidth
                variant="outlined"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />

              <TextField
                label="Study Duration (Optional)"
                fullWidth
                variant="outlined"
                value={editForm.duration || ""}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, duration: e.target.value }))
                }
                placeholder="e.g., 4 weeks, 2 months, 6 months"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleEditStudy}
              variant="contained"
              disabled={editLoading || !editForm.name.trim()}
            >
              {editLoading ? "Updating..." : "Update Study"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Participant</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this participant?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. All participant data will be
              permanently removed.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() =>
                participantToDelete &&
                handleDeleteParticipant(participantToDelete)
              }
              variant="contained"
            >
              Delete Participant
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Pending Participant Dialog */}
        <Dialog
          open={editPendingDialogOpen}
          onClose={() => setEditPendingDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Pending Participant</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Update the participant's information.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Phone Number (Optional)"
                fullWidth
                variant="outlined"
                value={editPendingForm.phoneNumber}
                onChange={(e) =>
                  setEditPendingForm((prev) => ({
                    ...prev,
                    phoneNumber: formatPhoneNumber(e.target.value),
                  }))
                }
                placeholder="(000)-0000-000"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditPendingDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditPendingParticipant}
              variant="contained"
              disabled={editLoading}
            >
              {editLoading ? "Updating..." : "Update Participant"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={editParticipantDialogOpen}
          onClose={() => setEditParticipantDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Participant Name</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Update the participant&apos;s first and last name.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                variant="outlined"
                value={editParticipantForm.firstName}
                onChange={(e) =>
                  setEditParticipantForm((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
              />
              <TextField
                label="Last Name"
                fullWidth
                variant="outlined"
                value={editParticipantForm.lastName}
                onChange={(e) =>
                  setEditParticipantForm((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditParticipantDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditParticipant}
              variant="contained"
              disabled={editLoading}
            >
              {editLoading ? "Updating..." : "Update Participant"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Pending Participant Dialog */}
        <Dialog
          open={deletePendingDialogOpen}
          onClose={() => setDeletePendingDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Remove Pending Participant</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to remove this participant from the study
              roster?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. The participant will no longer be
              able to enroll in the study.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletePendingDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                pendingParticipantToDelete &&
                handleDeletePendingParticipant(pendingParticipantToDelete)
              }
              variant="contained"
              color="error"
            >
              Remove Participant
            </Button>
          </DialogActions>
        </Dialog>

        {/* Participant Details Dialog */}
        <Dialog
          open={participantDetailsDialogOpen}
          onClose={() => setParticipantDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Participant Details</DialogTitle>
          <DialogContent>
            {selectedParticipantDetails && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Basic Information */}
                <Paper sx={{ p: 3, bgcolor: "primary.50" }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
                  >
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Participant ID:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, fontFamily: "monospace" }}
                      >
                        {selectedParticipantDetails.participantId}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Study Information */}
                <Paper sx={{ p: 3, bgcolor: "info.50" }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, color: "info.main" }}
                  >
                    Study Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Study Code:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, fontFamily: "monospace" }}
                      >
                        {selectedParticipantDetails.studyCode}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                      </Typography>
                      <Chip
                        label={selectedParticipantDetails.status}
                        color={
                          selectedParticipantDetails.status === "enrolled"
                            ? "success"
                            : "default"
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Enrolled Date:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedParticipantDetails.enrolledAt
                          ? new Date(
                              selectedParticipantDetails.enrolledAt,
                            ).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Authorized Date:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedParticipantDetails.authorizedAt
                          ? new Date(
                              selectedParticipantDetails.authorizedAt,
                            ).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Additional Information */}
                {selectedParticipantDetails.additionalInfo && (
                  <Paper sx={{ p: 3, bgcolor: "success.50" }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, mb: 2, color: "success.main" }}
                    >
                      Additional Information
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                    >
                      {selectedParticipantDetails.additionalInfo}
                    </Typography>
                  </Paper>
                )}

                {/* Documents */}
                {selectedParticipantDetails.documents &&
                  selectedParticipantDetails.documents.length > 0 && (
                    <Paper sx={{ p: 3, bgcolor: "warning.50" }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 2, color: "warning.main" }}
                      >
                        Uploaded Documents (
                        {selectedParticipantDetails.documents.length})
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {selectedParticipantDetails.documents.map(
                          (doc: any, index: number) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 1,
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                              }}
                            >
                              <AttachFile sx={{ color: "text.secondary" }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {doc.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {doc.type} • {Math.round(doc.size / 1024)} KB
                                  •{" "}
                                  {new Date(
                                    doc.uploadedAt,
                                  ).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </Box>
                          ),
                        )}
                      </Box>
                    </Paper>
                  )}

                {/* Information Completion Status */}
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: selectedParticipantDetails.informationCompleted
                      ? "success.50"
                      : "error.50",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: selectedParticipantDetails.informationCompleted
                        ? "success.main"
                        : "error.main",
                    }}
                  >
                    Information Status
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Chip
                      label={
                        selectedParticipantDetails.informationCompleted
                          ? "Completed"
                          : "Incomplete"
                      }
                      color={
                        selectedParticipantDetails.informationCompleted
                          ? "success"
                          : "error"
                      }
                      size="small"
                    />
                    {selectedParticipantDetails.informationCompletedAt && (
                      <Typography variant="body2" color="text.secondary">
                        Completed on{" "}
                        {new Date(
                          selectedParticipantDetails.informationCompletedAt,
                        ).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setParticipantDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Popup */}
        {notification && (
          <NotificationPopup
            notification={notification}
            onClose={clearNotification}
          />
        )}
      </Box>
    </Container>
  );
};

export default StudyDashboard;
